
import { oneShotLengthS } from "./motionloop";
import {
  DAMAGE_FONT_WIDTH,
  type DamageDraw,
  damageDigitAtlas,
  damageDraw,
  damageGlyphCell,
  damageGlyphFamily,
  sampleDamageCurve,
} from "../client/damagedigits";
import {
  type CombatView,
  type DeathMark,
  type Floater,
  type FloaterStyle,
  deathPhase,
  floaterAlpha,
  floaterOffsetPx,
  floaterPhase,
} from "../client/combat";
import { EntityKind, alignmentGrade, isChaotic, isDungeonDoor, isHorse, isKiller, isPet, isStealthed, type BagSlot_, type RemoteEntity, type Vitals, isMining } from "../net/connection";
import {
  empirePcNameColour,
  getNameColorEmpire,
  getNameColorMode,
} from "../ui/gameoptions";
import type { NameColorMode } from "../ui/systemoptions";
import {
  DEFAULT_PALETTE,
  type CreatureFrame,
  type CreaturePalette,
  GaitTracker,
  drawCreature,
} from "./creature";
import {
  creatureModelMatrix,
  CreatureMeshRenderer,
  PLAYER_MODEL_KEY,
  comboChainGapMs,
  comboSwingIntervalMs,
  swingPlaybackRatio,
  hairEntityId,
  isAttackClip,
  nextComboClip,
  comboMotionClip,
  comboChainFor,
  mountedAttackClip,
  type ClipEntry,
  type ClipName,
  type MotionMode,
  type MeshStatus,
} from "./creaturemesh";
import { actorMotion, isLock, needsFlyTarget } from "../client/actorbattle";
import { HIT_TYPE_NONE, hitReaction, type HitReaction } from "./motionreaction";
import { attackDataFor, loadAttackData, type AttackDataTable } from "./attackdata";
import {
  collisionRowsFor, loadCollisionVolumes, placeVolumes, refreshActorVolumes,
  type ActorVolumes, type CollisionVolumeTable, type PlacedSphere, type PlacedVolume,
} from "./collisionvolumes";
import { ScreenWaves, type WaveOffset } from "./screenwave";
import { ARROW_HEIGHT_MM, looksLikeBow, type Point } from "./arrowflight";
import { riderSkillClip, type RiderMode } from "./skillclip";
import { EFFECT_ATTACH_BONES, effectBoneFollowId, effectFollowId } from "./effectsanchor";
import { EffectRenderer, deathEffectsFor, hitEffectFor, stunEffect, type ArrowImpact } from "./effects";
import { GUILD_MARK_HEIGHT_PX, GUILD_MARK_WIDTH_PX, guildMarkPixels, guildMarkRect } from "./guildmark";
import { loadStoneSmoke, stoneSmokeAnchor, type StoneSmokeRow } from "./stonesmoke";
import { attachedEffectFollowId, loadAttachedEffects, type AttachedEffectRow } from "./attachedeffects";
import { loadMobColors, mobColorOf } from "./mobcolor";
import { castsCharacterShadow, type CharacterShadowMap } from "./charactershadow";
import { StoneSpawnMotions } from "./stonespawn";
import { FULL_SHADE, shadeAt, type ShadeRaster } from "./shade";
import {
  STEALTH_VISIBLE_ALPHA,
  type AlphaRamp,
  rampAlpha,
  spawnRamp,
  stealthRamp,
} from "./actoralpha";
import { actorShakeOffset } from "./actorshake";
import { CHAT_TAIL_COLOUR, CHAT_TAIL_NAME_LIFT_PX, chatTails } from "./chattails";
import {
  WeaponTrace,
  drawWeaponTrail,
  weaponTraceStrip,
  type WeaponTrailPoint,
} from "./weapontrail";
import {
  combatDecalAlpha,
  combatDecalPath,
  drawCombatDecal,
  hasCombatDecal,
} from "./combatdecal";
import {
  drawGesture,
  emoteClips,
  gestureVisual,
  type GestureMark,
} from "./gesture";

const HELD_PARTS: Readonly<Record<number, string>> = { [WearSlot.WEAPON]: "weapon" };
import {
  drawLoot,
  lootOwnership,
  OWNER_NAME_COLOUR,
} from "./loot";
import { WearSlot, bodyVnumWorn, hairCostumeVnumWorn, weaponVnumWorn } from "../wardrobe";
import { HEADING_STEPS } from "../sim/fixed";
import { SITING_INSTANCE_ID, type SitingGhost } from "../ui/guildsiting";
import { MM_PER_LEGACY_UNIT } from "./projectiles";
import { Gait, TICK_MS, type MoveState } from "../sim/movement";
import type { Camera } from "./camera";
import { lineOfSightClear, type HeightField } from "./heightfield";
import {
  barFillWidth,
  barGeometry,
  hasHealth,
  healthColour,
  levelColour,
  healthFraction,
  levelTail,
  LEVEL_TAIL_COLOUR,
} from "./health";

type Projected = ReturnType<Camera["project"]>;

export const ENTITY_COLOURS = {
  player: "#f2c14e",
  other: "#7fb2f0",
  otherIdle: "#5d80ad",
  monster: "#c9584a",
  monsterIdle: "#8f4038",
  npc: "#9d8cd0",
  stone: "#8e7fa8",
  stoneCracked: "#c07fb0",
  name: "rgba(233, 238, 245, 0.92)",
  killerName: "#b46400",
  chaoticName: "#c45ad4",
  nameShadow: "rgba(0, 0, 0, 0.75)",
  target: "#f2f2f2",
  barBack: "rgba(0, 0, 0, 0.6)",
  barEdge: "rgba(0, 0, 0, 0.85)",
  death: "#e8d9a0",
  loot: "#ffe9a8",
} as const;

export interface GuildBuildingStyle {
  readonly widthPx: number;
  readonly heightPx: number;
  readonly wall: string;
  readonly roof: string;
}

export function guildBuildingStyle(vnum: number): GuildBuildingStyle {
  const variant = Math.abs(Math.trunc(vnum)) % 3;
  return variant === 0
    ? { widthPx: 1.8, heightPx: 1.45, wall: "#c58f58", roof: "#754334" }
    : variant === 1
      ? { widthPx: 1.55, heightPx: 1.7, wall: "#b6a06b", roof: "#52627b" }
      : { widthPx: 2.1, heightPx: 1.25, wall: "#9d6b4f", roof: "#493f65" };
}

/** `c_iGuildSymbolRace` (`InstanceBase.cpp:833`): the building that wears its guild's symbol. */
export const GUILD_SYMBOL_RACE = 14200;

export const ALIGNMENT_TITLES: readonly string[] = [
  "Chivalric",
  "Noble",
  "Good",
  "Friendly",
  "",
  "Aggressive",
  "Fraudulent",
  "Malicious",
  "Cruel",
];

export const ALIGNMENT_TITLE_COLOURS: readonly string[] = [
  "#00ccff", // 0 -- TITLE_RGB_GOOD_4 = (0, 204, 255), colorinfo.py:29
  "#0090ff", // 1 -- TITLE_RGB_GOOD_3 = (0, 144, 255), colorinfo.py:30
  "#5c6eff", // 2 -- TITLE_RGB_GOOD_2 = (92, 110, 255), colorinfo.py:31
  "#9b9bff", // 3 -- TITLE_RGB_GOOD_1 = (155, 155, 255), colorinfo.py:32
  "#ffffff", // 4 -- TITLE_RGB_NORMAL = (255, 255, 255), colorinfo.py:33
  "#cf7500", // 5 -- TITLE_RGB_EVIL_1 = (207, 117, 0), colorinfo.py:34
  "#eb5300", // 6 -- TITLE_RGB_EVIL_2 = (235, 83, 0), colorinfo.py:35
  "#e30000", // 7 -- TITLE_RGB_EVIL_3 = (227, 0, 0), colorinfo.py:36
  "#ff0000", // 8 -- TITLE_RGB_EVIL_4 = (255, 0, 0), colorinfo.py:37
];

/** The title line over one character's head: its text and its colour, or an
 *  empty text when the grade is `TITLE_NONE` and the original detaches the
 *  line (`RefreshTextTail`, `UserInterface/InstanceBaseEffect.cpp:666-683`). */
export function alignmentTitleFor(
  entity: { readonly flags: number },
): { readonly text: string; readonly colour: string } {
  const grade = alignmentGrade(entity);
  return { text: ALIGNMENT_TITLES[grade], colour: ALIGNMENT_TITLE_COLOURS[grade] };
}

export const UNNAMED_GUILD = "Noname";

export function guildNameFor(
  entity: { readonly guildId: number },
  guildNames?: ReadonlyMap<number, string>,
): string {
  if (entity.guildId === 0) return "";
  return guildNames?.get(entity.guildId) ?? UNNAMED_GUILD;
}

export function nameColourFor(
  entity: { readonly flags: number },
  ordinary: string,
): string {
  if (isKiller(entity)) return ENTITY_COLOURS.killerName;
  if (isChaotic(entity)) return ENTITY_COLOURS.chaoticName;
  return ordinary;
}

export function ordinaryNameColour(
  kind: number,
  fallback: string,
  mode: NameColorMode = getNameColorMode(),
  empire: number = getNameColorEmpire(),
): string {
  if (mode === "empire" && kind === EntityKind.PLAYER) {
    return empirePcNameColour(empire);
  }
  return fallback;
}

/** Damage numbers are read at a glance and never dwelt on, so the difference
 *  between them has to be carried by colour and weight rather than by wording. */
const FLOATER_COLOURS: Record<FloaterStyle, string> = {
  hit: "#f4f7fb",
  crit: "#ffd45e",
  miss: "#9aa7b4",
  block: "#8fc9e8",
};

/** The local player's colours: the same gold the ring and the nameplate use, so
 *  the three read as one character rather than as three overlapping marks. */
const PLAYER_PALETTE = { body: "#f2c14e", dark: "#a37f2a" } as const;

/** The stand-in colour for a horse, used only where the creature pack has no
 *  mount body. Brown, and deliberately outside the threat palette: a mount is
 *  scenery, and nobody should be sizing one up. */
const MOUNT_PALETTE = { body: "#8b6b45", dark: "#5a4327" } as const;

/** The mount's vertical contribution and which canvas owns its pixels. A
 * missing mount is painted on the 2D overlay; a mesh rider below that overlay
 * would be composited underneath the horse, so the caller keeps both bodies in
 * the same layer for that frame. */
interface MountDrawResult {
  readonly liftMm: number;
  readonly fallbackOverlay: boolean;
}

function shade(colour: string): string {
  const match = /^#([0-9a-f]{6})$/i.exec(colour);
  if (match === null) return colour;
  const value = Number.parseInt(match[1], 16);
  const channel = (shift: number) =>
    Math.round(((value >> shift) & 0xff) * 0.62)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(16)}${channel(8)}${channel(0)}`;
}

/** The vertical space one nameplate occupies -- `drawName` draws 12px text on
 *  a `bottom` baseline, so this is what has to be cleared to sit above it. */
const PLATE_HEIGHT_PX = 13;

/** The one font every tail over a head is drawn in. The original gives the
 *  name, the guild name, the title, the level and the chat tail the same
 *  `ms_pFont` (`UserInterface/PythonTextTail.cpp:513`, `:553`, `:902`, `:956`,
 *  `:617`), so they are one style here too. */
const PLATE_FONT = "600 12px system-ui, sans-serif";

const PLATE_NAME_SHIFT_PX = 4;
const PLATE_NAME_SHIFT_TITLED_PX = 8;

/** The guild name's own line, `c_fyGuildNamePosition = 15.0f` above the name's
 *  baseline (`UserInterface/PythonTextTail.cpp:20`, `:222-232`; screen y grows
 *  downward, so `y - 15` is above). */
const GUILD_TAIL_LIFT_PX = 15;

/** `c_TextTail_Guild_Name_Color = 0xFFEFD3FF` (`UserInterface/PythonTextTail.cpp:18`). */
const GUILD_TAIL_COLOUR = "#efd3ff";

const GUILD_TAIL_SHIFT_PX = 1;

const GM_MARK_SRC = "/ui/gm_mark.png";
const gmMarkImage = typeof Image === "undefined" ? null : new Image();
if (gmMarkImage !== null) gmMarkImage.src = GM_MARK_SRC;

/** Drawn at the source aspect (246:127), sized so it reads at a glance without
 *  covering the character under it. */
const GM_MARK_WIDTH_PX = 46; // ymirred.tga is 246x127 (whisperdialog.py:67-78); 46 px keeps its aspect at nameplate height
const GM_MARK_HEIGHT_PX = Math.round((GM_MARK_WIDTH_PX * 127) / 246);
/** The gap between the mark's underside and the nameplate below it. */
const GM_MARK_GAP_PX = 2;

const PLAYER_RADIUS_MM = 500;
const MONSTER_RADIUS_MM = 620;
/** A stone is a landmark: it should read as one from across a field, and it
 *  is the only thing on the map you can see before you can see what it is. */
const STONE_RADIUS_MM = 1_400;
/** Loot is drawn a little larger than it "is". A sword lying in grass is
 *  physically small and visually the most important thing on the screen, and
 *  the second fact wins. */
const LOOT_RADIUS_MM = 520;
/** The hit burst's authored emitter height (`blow_1.mse`'s
 * `TimeEventPosition`). The Canvas2D flash is the short-lived readability
 * cue for the same hit, so it must share the burst's elevation rather than
 * landing at the creature's feet. */
const HIT_FLASH_HEIGHT_MM = 1_110;

/** The screen position where a gesture glyph starts above its actor. */
export function gestureAnchorY(
  groundY: number,
  radius: number,
  scale: number,
  modelTopY: number | null,
): number {
  const fallback = groundY - 28 * scale;
  if (modelTopY === null || !Number.isFinite(modelTopY)) return fallback;
  return Math.min(modelTopY, groundY - radius) - 10;
}

/** The screen origin for a damage number, above a real body when one exists. */
export function floaterAnchorY(
  groundY: number,
  radius: number,
  modelTopY: number | null,
): number {
  if (modelTopY === null || !Number.isFinite(modelTopY)) return groundY;
  return Math.min(modelTopY, groundY - radius) - 10;
}

/** The screen centre for the short hit flash, at the effect's authored height.
 * Keep the old ground centre when a body is a Canvas2D fallback or its
 * projection is behind the camera. */
export function flashAnchorY(
  groundY: number,
  radius: number,
  effectY: number | null,
): number {
  if (effectY === null || !Number.isFinite(effectY)) return groundY;
  return Math.min(effectY, groundY - radius);
}

/** How long a slain monster's body lies before `dead_event` removes it (`char_battle.cpp:1458`). */
const CORPSE_HOLD_MS = 10_000; // char_battle.cpp:1458

/** Fallback chain-continuation gap when the previous clip carries no
 *  `ComboInputData.InputLimitTime`. Twice `REFERENCE_SWING_MS` (1 s at
 *  attack_speed 100) -- the pre-MSA guess this used to be for every swing. */
export const COMBO_CHAIN_GAP_MS = 2_000;

// ------------------------------------------------------------- footfall dust

export const DUST_GAP_MM = 250 * MM_PER_LEGACY_UNIT;
export const HORSE_DUST_GAP_MM = 500 * MM_PER_LEGACY_UNIT;

export const PC_DUST_RANGE_MM = 2000 * MM_PER_LEGACY_UNIT;
export const NPC_DUST_RANGE_MM = 1000 * MM_PER_LEGACY_UNIT;

/** One character as the dust rule sees it. */
export interface DustWalker {
  readonly id: number;
  readonly xMm: number;
  readonly yMm: number;
  /** False for a standing character. `CInstanceBase::Update` only reaches the
   *  dust block inside `if (IsWalking() || IsUsingMovingSkill())`
   *  (`InstanceBase.cpp:1766`), so a character who is not going anywhere
   *  raises nothing however far he was teleported. */
  readonly walking: boolean;
  /** `IsPC()`, which picks the range (`__IsInDustRange`), not the effect. */
  readonly isPlayerCharacter: boolean;
  /** `IsMountingHorse()`, which picks the effect and the gap
   *  (`InstanceBase.cpp:1795-1809`). */
  readonly mounted: boolean;
}

/** One puff owed this frame: the manifest key and where it goes. */
export interface DustPuff {
  readonly id: number;
  readonly effect: string;
  readonly xMm: number;
  readonly yMm: number;
}

export class DustTrail {
  /** Entity id -> `m_kPPosDust`. */
  private readonly at = new Map<number, { x: number; y: number }>();

  /** The puffs owed this frame, given where the player is standing. */
  step(
    walkers: Iterable<DustWalker>,
    mainXMm: number,
    mainYMm: number,
  ): DustPuff[] {
    const puffs: DustPuff[] = [];
    for (const walker of walkers) {
      if (!walker.walking) continue;
      const range = walker.isPlayerCharacter ? PC_DUST_RANGE_MM : NPC_DUST_RANGE_MM;
      if (Math.hypot(walker.xMm - mainXMm, walker.yMm - mainYMm) > range) continue;
      // The origin, for an instance never seen before -- see the class note.
      const mark = this.at.get(walker.id) ?? { x: 0, y: 0 };
      const gap = walker.mounted ? HORSE_DUST_GAP_MM : DUST_GAP_MM;
      if (Math.hypot(walker.xMm - mark.x, walker.yMm - mark.y) <= gap) continue;
      this.at.set(walker.id, { x: walker.xMm, y: walker.yMm });
      puffs.push({
        id: walker.id,
        effect: walker.mounted ? "horse_dust" : "dust",
        xMm: walker.xMm,
        yMm: walker.yMm,
      });
    }
    return puffs;
  }

  /** Forget everyone not in `live`, for `GaitTracker.prune`'s reason. */
  prune(live: ReadonlySet<number>): void {
    for (const id of this.at.keys()) {
      if (!live.has(id)) this.at.delete(id);
    }
  }
}

export function advanceComboStep(
  previous: { readonly step: number; readonly lastMs: number } | undefined,
  bornMs: number,
  inputLimitMs: number = COMBO_CHAIN_GAP_MS,
): { readonly step: number; readonly lastMs: number } {
  const limit = inputLimitMs > 0 ? inputLimitMs : COMBO_CHAIN_GAP_MS;
  const step = previous !== undefined && bornMs - previous.lastMs < limit
    ? previous.step + 1 : 1;
  return { step, lastMs: bornMs };
}

/** The clips a landed blow can start on its victim; see `hitReaction`. */
const REACTION_CLIPS: readonly ClipName[] = ["damage", "damageBack", "knockdown", "standUp", "knockdownBack", "standUpBack"];

interface Seen {
  readonly key: string;
  readonly xMm: number;
  readonly yMm: number;
  readonly heading: number;
  /** `EntityKind`, remembered with the rest so the effect fired when something
   *  dies can be the one its kind calls for. A `DeathMark` says who died and
   *  where, not what it was, and by the time it is read the entity is gone. */
  readonly kind: number;
  readonly palette: CreaturePalette;
  /** What he was holding, as `drawBody` takes it. Remembered with the rest so
   *  a corpse keeps its sword: the entity is gone by the time the body is
   *  drawn, and looking the weapon up again would find nothing. */
  readonly held: Readonly<Record<string, string>> | null;
  /** The combat clip selected by the authoritative worn weapon vnum. This is
   *  remembered with the held model so the swing and the sword cannot disagree
   *  between a bag frame and the next render frame. */
  readonly attackClip: ClipName;
  /** The `MOB_COLOR` the body is drawn modulated by, kept so a corpse keeps it. */
  readonly mobColor: number;
}

/** Whether a fallen body is drawn as a corpse this frame. One the server is
 *  still sending is drawn by the living path -- same id, same mesh instance,
 *  same clip carrying on -- and drawing it here as well would double it. The
 *  server drops a slain body on the tick that killed it unless a reviver's
 *  `dead_event` holds it (`char_battle.cpp:1452-1454`); a body held past that
 *  tick and then dropped was destroyed by that event (`char_battle.cpp:483`),
 *  and nothing lies on. */
export function corpseLies(corpse: Corpse, live: boolean, nowMs: number): boolean {
  if (nowMs >= corpse.untilMs) return false;
  if (live) {
    if (nowMs - corpse.fellMs > TICK_MS) corpse.kept = true;
    return false;
  }
  return !corpse.kept;
}

interface Corpse extends Seen {
  readonly entityId: number;
  readonly untilMs: number;
  /** When the killing blow arrived. */
  readonly fellMs: number;
  /** Whether the server went on sending the fallen body past the tick that
   *  killed it: a corpse waiting for a reviver's `dead_event`
   *  (`char_battle.cpp:1452-1454`), which ends it (`char_battle.cpp:483`). */
  kept: boolean;
}

/** How tall a character is taken to be while the pack has not answered with a
 *  real model height yet, in millimetres. Only a stand-in for the mesh's own
 *  bounding height, used by the line of sight test and by `headAnchor`, and
 *  replaced by the measured height the frame after the body loads. */
const FALLBACK_BODY_HEIGHT_MM = 800;

const BAR_WIDTH_PX = 34;
const BAR_HEIGHT_PX = 4;
const PLAYER_BAR_WIDTH_PX = 62;
const PLAYER_BAR_HEIGHT_PX = 7;

export interface RenderFrame {
  player: MoveState;
  /** The local entity's server id, or -1 before Welcome. Needed so a hit on the
   *  player flashes the player and not whichever entity happens to share the id. */
  playerId: number;
  playerName: string;
  /** The authoritative snapshot clock. Loot claim deadlines are expressed on
   *  this clock, never against browser wall time. */
  serverTick: number;
  playerVitals: Vitals;
  others: readonly RemoteEntity[];
  /** The entity the player has selected, drawn with a ring. */
  targetId: number | null;
  combat: CombatView;
  /** Server-confirmed emotes still within their short visual lifetime. */
  gestures: readonly GestureMark[];
  /** One-shot motions this client's own events started on a body -- the
   *  fishing act's throw, tug, cancel, catch and fail. */
  motions?: readonly MotionMark[];
  /** The loops the local player's body is drawn in while nothing about his
   *  armour decides it: a rod's, a cast line's, or a miner's. */
  playerMotionMode?: MotionMode;
  /** The bodies whose line is out: `FishingAct` started them and nothing has
   *  ended them yet. */
  anglers?: { has(entityId: number): boolean };
  playerWorn?: readonly BagSlot_[];
  /** Which of the eight bodies the local character wears, from his own
   *  snapshot (`Connection.selfRace`). Optional, and absent means race 0 --
   *  which is a real body, the novice warrior, so a frame rendered before the
   *  first snapshot of ourselves draws a character rather than nothing. */
  playerRace?: number;
  playerMount?: number;
  /** Public flags for the local player from their own snapshot. */
  playerFlags?: number;
  /** `EntityDelta.affect_flags` off the local player's own snapshot. */
  playerAffectFlags?: number;
  /** The local player's own operator level (`Connection.selfGmLevel`), or
   *  0/absent for a player. Optional so an embedding written before the mark
   *  existed draws what it always drew. */
  playerGmLevel?: number;
  playerGuildName?: string;
  /** The local player's guild id, 0 or absent in none. */
  playerGuildId?: number;
  guildNames?: ReadonlyMap<number, string>;
  /** Served guild signs by guild id, the pixels `CGraphicMarkInstance` draws
   *  beside a member's guild name. */
  guildEmblems?: ReadonlyMap<number, readonly number[]>;
  /** Served guild symbols by guild id, decoded: the picture a guild insignia
   *  wears (`InstanceBase.cpp:832-838`). */
  guildSymbols?: ReadonlyMap<number, CanvasImageSource>;
  /** Each ground item in view by entity id: the vnum its drop model is found
   *  by and when this client was told it (`PythonItem.cpp:298-445`). */
  groundItems?: ReadonlyMap<number, { readonly vnum: number; readonly seenMs: number }>;
  /** The guild building being sited, drawn translucent before it exists
   *  (`BuildGuildBuildingWindow.__CreateInstance`, `root/uiguild.py:2248-2274`). */
  sitingGhost?: SitingGhost | null;
  /** Where the event camera holds the view instead of the player
   *  (`CAMERA_MODE_STAND`, `PythonApplicationCamera.cpp:48-56`). */
  cameraAnchor?: { readonly xMm: number; readonly yMm: number } | null;
  alwaysShowNames?: boolean;
  showDamage?: boolean;
  showSalesText?: boolean;
  showNameHeld?: boolean;
  nameColorMode?: NameColorMode;
  /**
   * Kingdom used when `nameColorMode` is `"empire"`. Optional; falls back to
   * the live presentation empire (Shinsoo by default).
   */
  nameColorEmpire?: number;
  /** Live, public stall boards by seller entity id.  `StallSign` is a
   * server-authored map-wide frame; this map is only its already-received
   * client-side presentation cache. */
  stallSigns?: ReadonlyMap<number, string>;
  /** The frame's clock, so every effect in it agrees on what time it is. Passed
   *  rather than read, for the same reason `combat.ts` takes it: an animation
   *  driven by a hidden clock cannot be stepped. */
  nowMs: number;
  dtMs: number;
}

/** The loops another player's body stands in: `riderMotionMode` over the
 *  fishing wait `StartFishing` pushes while the body's line is out
 *  (`InstanceBaseMotion.cpp:66`), or the general set. */
export function remoteMotionMode(dressed: boolean, fishing: boolean, mounted: boolean): MotionMode {
  return riderMotionMode(dressed, mounted, fishing ? "fishingLine" : "general");
}

/** `RefreshState`'s precedence: a wedding dress's own set, then the horse set,
 *  which a rod or a pick-axe gives way to, then the mode the body is in on
 *  foot (`InstanceBase.cpp:2870-2896`). */
export function riderMotionMode(dressed: boolean, mounted: boolean, onFoot: MotionMode): MotionMode {
  if (dressed) return "wedding";
  return mounted ? "horse" : onFoot;
}

/** One one-shot clip started on a body by an event, keyed once. */
export interface MotionMark {
  readonly key: number;
  readonly entityId: number;
  readonly clip: ClipName;
  readonly bornMs: number;
  /** The one-shot queued behind `clip`, as `PushOnceMotion` queues it. */
  readonly then?: ClipName | null;
}

/** The two lookups `bodyKeyFor` needs, which is all of `CreatureMeshRenderer`
 *  it needs. Named as an interface so a test can answer them without a canvas,
 *  a WebGL context and a megabyte of geometry. */
export interface BodyLookup {
  baseBodyKey(race: number): string | null;
  bodyModelKey(vnum: number, race?: number): string | null;
  /** The model a pet mob vnum draws as, or null for every other vnum. */
  petModelKey?(vnum: number): string | null;
}

export function bodyKeyFor(
  meshes: BodyLookup,
  armourVnum: number,
  race: number,
): string {
  const bare = meshes.baseBodyKey(race) ?? PLAYER_MODEL_KEY;
  if (armourVnum === 0) return bare;
  return meshes.bodyModelKey(armourVnum, race) ?? bare;
}

/** The model one entity asks the creature pack for: a polymorph shape, the
 *  mount a horse flag names, a player's dressed body, or the monster's own
 *  name -- `CInstanceBase::SetRace` keyed on the same four cases. */
export function meshKeyFor(meshes: BodyLookup, entity: RemoteEntity): string {
  if (entity.polymorph !== "") return entity.polymorph;
  if (isHorse(entity)) return MOUNT_MESH_KEYS[entity.horseVnum] ?? entity.name;
  if (entity.kind === EntityKind.PLAYER) return bodyKeyFor(meshes, entity.armour, entity.race);
  // A pet carries its owner's name (`PetSystem.cpp:79-96`); its body is race
  // `wRaceNum` (`char.cpp:813`) as npclist names it (`RaceManager.cpp:140-155`).
  return (entity.mobVnum ? meshes.petModelKey?.(entity.mobVnum) : null) ?? entity.name;
}

export const MOUNT_MESH_KEYS: Readonly<Record<number, string>> = {
  // The rented horse `pc.mount(20030, 600)` puts a ticket-holder on
  // (`horse_ride.quest:20`). `npclist.txt:1201` gives race 20030 the pony's
  // own race name, so it is that body rather than a fourth one.
  20030: "pony_normal",  // npclist.txt:1201
  20101: "pony_normal",
  20104: "horse_normal",
  20107: "horse2_normal",
};

export const MOUNT_FALLBACK_HEIGHT_MM = 1200;

/** `__SetMotion` on a rider (`ActorInstanceMotion.cpp:707-728`): the body plays
 *  `clip` as its motion mode `mode` resolves it (`riderSkillClip`), and the horse
 *  under it, drawn as `-riderId`, is set the same motion slot, which it plays
 *  only where its own race registers it (`playOnce`). A slot the rider's mode
 *  resolves to no file plays on neither (`ActorInstanceMotion.cpp:345-350`). */
export function playRiderMotion(
  meshes: Pick<CreatureMeshRenderer, "playOnce" | "clipEntry">,
  riderId: number,
  riderKey: string,
  mount: number,
  clip: ClipName,
  bornMs: number,
  isMain: boolean,
  then: ClipName | null,
  mode: RiderMode = "Horse",
): void {
  const own = mount === 0 ? clip : riderSkillClip(clip, mode, (named) => meshes.clipEntry(riderKey, named) !== null);
  if (own === null) return;
  if (then === null) meshes.playOnce(riderId, riderKey, own, bornMs, isMain);
  else meshes.playOnce(riderId, riderKey, own, bornMs, isMain, 1, then);
  const horse = mount === 0 ? undefined : MOUNT_MESH_KEYS[mount];
  if (horse === undefined) return;
  if (then === null) meshes.playOnce(-riderId, horse, clip, bornMs);
  else meshes.playOnce(-riderId, horse, clip, bornMs, false, 1, then); // ActorInstanceMotion.cpp:718
}

/** The motion mode a rider holding `weaponVnum` is in, as `__RefreshMotionMode`
 *  switches on the weapon's sub-type while mounted (`InstanceBase.cpp:2896-2929`):
 *  a bow by its left-hand-only hold (`looksLikeBow`), a melee weapon by its
 *  combo mode's mounted twin (`mountedAttackClip`), and `MODE_HORSE` for
 *  anything else. */
export function mountedModeFor(
  meshes: Pick<CreatureMeshRenderer, "weaponAttackClip" | "itemHands">,
  weaponVnum: number,
): RiderMode {
  if (weaponVnum === 0) return "Horse"; // InstanceBase.cpp:2923-2925
  if (looksLikeBow(meshes.itemHands(weaponVnum))) return "HorseBow"; // InstanceBase.cpp:2919-2921
  const mounted = mountedAttackClip(meshes.weaponAttackClip(weaponVnum));
  return mounted.startsWith("attackHorse") ? mounted.slice("attack".length) as RiderMode : "Horse";
}

/** What `heldPartsFor` needs of the mesh renderer: a model for a vnum, and
 *  which hands that vnum occupies. */
export interface HeldLookup {
  itemModelKey(vnum: number): string | null;
  itemHands(vnum: number): readonly string[];
  /** Conditional hand placement while mounted; absent on pre-mount packs. */
  itemMountedHands?(vnum: number): readonly string[];
}

/** The four bouquets, the only held items a wedding dress still shows
 *  (`InstanceBase.cpp:900-919`). */
export const BOUQUET_VNUMS: ReadonlySet<number> = new Set([50201, 50202, 50203, 50204]);

/** The shape number of the wedding dress (`InstanceBase.cpp:921-925`). */
export const WEDDING_DRESS_SHAPE = 201;

export function heldPartsFor(
  meshes: HeldLookup,
  weaponVnum: number,
  mounted = false,
  wearingDress = false,
): Readonly<Record<string, string>> | null {
  if (weaponVnum === 0) return null;
  if (wearingDress && !BOUQUET_VNUMS.has(weaponVnum)) return null;
  const model = meshes.itemModelKey(weaponVnum);
  if (model === null) return null;

  const hands = mounted && meshes.itemMountedHands !== undefined
    ? meshes.itemMountedHands(weaponVnum)
    : meshes.itemHands(weaponVnum);
  if (hands.length === 0) return { [HELD_PARTS[WearSlot.WEAPON]]: model };

  const held: Record<string, string> = {};
  // The same model in both hands for a dagger. One mesh drawn twice, at two
  // bones, which is exactly what the original does -- there is no second,
  // mirrored model, and `attach` is keyed by part rather than by item.
  for (const part of hands) held[part] = model;
  return held;
}

/** As much of a character as deciding whether they just shot something needs:
 *  where they were standing and what was in their hands. Both are already in
 *  `Seen`, which is what the painter passes. */
export interface Archer {
  readonly xMm: number;
  readonly yMm: number;
  readonly held: Readonly<Record<string, string>> | null;
}

/** One shot: the two ends of the flight, and what arriving sets off. */
export interface ArrowShot {
  readonly from: Point;
  readonly to: Point;
  readonly impact: ArrowImpact | null;
}

export function arrowShotFor(
  archer: Archer | undefined,
  floater: Floater,
  struckHeadingRad: number,
  groundAtMm: (xMm: number, yMm: number) => number,
): ArrowShot | null {
  if (archer === undefined || archer.held === null) return null;
  if (!looksLikeBow(Object.keys(archer.held))) return null;

  const landing = groundAtMm(floater.xMm, floater.yMm);
  const effect = hitEffectFor(floater.style);
  return {
    from: {
      xMm: archer.xMm,
      yMm: archer.yMm,
      zMm: groundAtMm(archer.xMm, archer.yMm) + ARROW_HEIGHT_MM,
    },
    to: { xMm: floater.xMm, yMm: floater.yMm, zMm: landing + ARROW_HEIGHT_MM },
    impact:
      effect === null
        ? null
        : {
            effect,
            options: {
              xMm: floater.xMm,
              yMm: floater.yMm,
              zMm: landing,
              headingRad: struckHeadingRad,
            },
          },
  };
}


export interface RenderedCharacterParts {
  readonly frame: number;
  readonly body: string;
  readonly hair: string | null;
  readonly bodyDraws: number;
  readonly hairDraws: number;
  readonly hairBones: number;
  /** Item-model keys handed to the body's authored attachment bones. Empty for
   *  a bare-handed character; populated only after the body mesh draw succeeds. */
  readonly held: readonly string[];
  /** The shared animation clock sampled after both body and hair were drawn. */
  readonly activeClip: ClipName | null;
  readonly attackClip: ClipName;
  readonly heldOffsetMm: number | null;
}

/** Draws the living scene into a 2D context, through a camera someone else owns. */
export class EntityPainter {
  private readonly gait = new GaitTracker();
  /** `m_kPPosDust` per character -- see `DustTrail`. */
  private readonly dust = new DustTrail();
  /** Reused each frame rather than allocated: this runs sixty times a second. */
  private readonly live = new Set<number>();
  /** Species name -> the colours sampled from its skin. Empty until the palette
   *  is fetched, and every lookup falls back, so a missing file costs authentic
   *  colour rather than a blank screen. */
  private palettes: Readonly<Record<string, CreaturePalette>> = {};
  /** Real geometry for whatever `tools/build_creature_meshes.py` converted --
   *  the WebGL renderer shares the terrain context, while direct Canvas2D
   *  callers retain the standalone-layer fallback described in `creaturemesh.ts`. */
  private readonly meshes: CreatureMeshRenderer;
  /** The particle layer. Public because it is the one part of the renderer a
   *  harness has to be able to ask "is anything on screen yet"; see
   *  `tools/e2e/effects.mjs`. */
  readonly effects: EffectRenderer;
  /** One console line per (key, reason) the first time it is seen, so a
   *  species drawn as a silhouette says why exactly once instead of once a
   *  frame -- see `logFallback`. */
  private readonly warnedFallback = new Set<string>();
  private readonly flinched = new Set<number>();
  /** Death marks already turned into a death animation, by mark key -- which
   *  `combat.ts` allocates once per death, so a monster killed twice in one
   *  session (respawned, killed again) is two marks and two falls. */
  private readonly fallen = new Set<number>();
  /** Swings already turned into an attack animation, by swing key -- one per
   *  blow, so a monster that hits twice in a second swings twice rather than
   *  having the second swallowed as "already playing". */
  private readonly swung = new Set<number>();
  /** Gesture and motion keys whose clips have been started. */
  private readonly gestured = new Set<number>();
  private readonly moved = new Set<number>();
  private readonly comboStep = new Map<number, { step: number; lastMs: number; clip?: ClipName }>();
  /** Impacts already turned into a burst of sparks, by impact key.
   *  Separate from `flinched` because the two do not fire on the same events:
   *  a hit on a creature this client has never drawn still deserves its
   *  sparks even though there is no mesh to flinch. */
  private readonly sparked = new Set<number>();
  /** Damage floaters already turned into an arrow in flight, by floater key.
   *  Its own set rather than `sparked`'s: `combat.ts` allocates impact keys
   *  and floater keys from two independent counters, so one set would have
   *  impact 0 swallowing floater 0. */
  private readonly loosed = new Set<number>();
  /** Death marks already turned into a burst of particles, by mark key.
   *  Separate from `fallen` because the two can disagree: a species with no
   *  death clip still shatters, and a stone plays a different effect from a
   *  wolf. */
  private readonly shattered = new Set<number>();
  /** Death-motion effects waiting for their `StartingTime`, in queue order. */
  private dueEffects: Array<{ atMs: number; effect: string; at: { xMm: number; yMm: number; zMm: number } }> = [];
  /** Casts already turned into an effect, by cast key. */
  private readonly castKeys = new Set<number>();
  /** Server-named canned effects already played, by mark key. */
  private readonly specialKeys = new Set<number>();
  /** Bodies outliving their entities. See `Corpse`. */
  private readonly corpses = new Map<number, Corpse>();
  /** Where each entity was the last time it was drawn, so a corpse can be
   *  built from the frame before the entity vanished. Pruned with the rest of
   *  the per-entity tables at the end of `draw`. */
  private readonly lastSeen = new Map<number, Seen>();
  private readonly screenWaves = new ScreenWaves();
  /** The material-alpha ramp each body is currently on: the spawn fade the
   *  first frame it is drawn, then whichever ramp its stealth affect started.
   *  See `render/actoralpha.ts`. Pruned with `lastSeen`. */
  private readonly alphaRamps = new Map<number, AlphaRamp>();
  /** The weapon attach bone of every body drawn with something in hand, world
   *  millimetres, as of its last draw -- `CreatureMeshRenderer.weaponAnchor`
   *  read straight after the draw. `render/framefx.ts` hangs the refine glow
   *  and the affect loops on it. Pruned with `lastSeen`. */
  private readonly weaponAnchors = new Map<number, { xMm: number; yMm: number; zMm: number }>();
  /** The off-hand copy of the same, for a body that drew a weapon in both
   *  hands this frame -- today, only a dagger. See
   *  `CreatureMeshRenderer.weaponAnchorLeft`. Pruned alongside `weaponAnchors`. */
  private readonly weaponAnchorsLeft = new Map<number, { xMm: number; yMm: number; zMm: number }>();
  private renderedCharacters = new Map<number, RenderedCharacterParts>();
  private renderedFrame = 0;
  /** The map's shade raster, once `terrain_shade.png` has decoded. */
  private shadeRaster: ShadeRaster | null = null;
  private dropModelIds: ReadonlySet<number> = new Set();
  private insigniaModelIds: ReadonlySet<number> = new Set();
  /** Each drawn actor's material colour, as last sampled while it moved. */
  private readonly shades = new Map<number, [number, number, number]>();
  /** Each stone species' smoke, once `creaturemesh/smoke.json` has loaded. */
  private stoneSmoke: ReadonlyMap<string, StoneSmokeRow> = new Map();
  /** Each species' `.msm`-attached effects, once `creaturemesh/attached.json`
   *  has loaded. */
  private attachedEffects: ReadonlyMap<string, readonly AttachedEffectRow[]> = new Map();
  /** Each armed character's right- and left-hand weapon traces
   *  (`CActorInstance::m_WeaponTraceVector`), and the clock they are updated by. */
  private weaponTraces?: Map<number, { readonly right: WeaponTrace; readonly left: WeaponTrace }>;
  private traceNowMs = 0;
  /** Each monster vnum's `MOB_COLOR`, once `creaturemesh/colors.json` has loaded. */
  private mobColors: ReadonlyMap<number, number> = new Map();
  /** Every body's `.msm` collision blocks, once `creaturemesh/collision.json` has loaded. */
  private collisionVolumes: CollisionVolumeTable | null = null;
  /** Every attack clip's blow data, once `creaturemesh/attackdata.json` has loaded. */
  private attackData: AttackDataTable | null = null;
  /** Each drawn body's `m_BodyPointInstanceList`/`m_DefendingPointInstanceList`
   *  for the body it was built for, and its defending spheres as its last draw
   *  placed them. */
  private readonly volumes = new Map<number, { key: string; kept: ActorVolumes; defending: PlacedVolume[] }>();
  /** `m_fInvisibleTime` per body, in this painter's clock (`ActorInstance.h:736`). */
  private readonly invisibleUntilMs = new Map<number, number>();
  /** Each stone's SPAWN motion, for the stones that arrived playing one. */
  private readonly stoneSpawns = new StoneSpawnMotions();
  /** Each served sign's image, keyed by the sign array the frame carries. */
  private readonly guildMarks = new WeakMap<readonly number[], CanvasImageSource>();
  private readonly markRects = new Map<
    number,
    { x: number; y: number; width: number; height: number }
  >();

  constructor(
    private readonly ctx: CanvasRenderingContext2D,
    private readonly camera: Camera,
    private readonly heightField: HeightField,
    sharedGl: WebGL2RenderingContext | null = null,
    sharedCanvas: HTMLCanvasElement | null = null,
  ) {
    this.meshes = new CreatureMeshRenderer(ctx.canvas, sharedGl, sharedCanvas);
    this.effects = new EffectRenderer(ctx.canvas, sharedGl, sharedCanvas);
    if (typeof document !== "undefined") {
      void loadStoneSmoke().then((rows) => {
        this.stoneSmoke = rows;
      });
      void loadAttachedEffects().then((rows) => {
        this.attachedEffects = rows;
      });
      void loadMobColors().then((rows) => {
        this.mobColors = rows;
      });
      void loadCollisionVolumes().then((table) => {
        this.collisionVolumes = table;
      });
      void loadAttackData().then((table) => {
        this.attackData = table;
      });
    }
  }

  /** The character shadow target every casting body is drawn into, or null. */
  setCharacterShadow(map: CharacterShadowMap | null): void {
    this.meshes.setCharacterShadow(map);
  }

  /** Whether a living body at this spot casts this frame, measured from the
   *  main instance (`InstanceBase.cpp:2004-2026`). */
  private castsShadowAt(frame: RenderFrame, xMm: number, yMm: number, alpha: number, door = false): boolean {
    return castsCharacterShadow({
      distanceMm: Math.hypot(xMm - frame.player.xMm, yMm - frame.player.yMm),
      alpha,
      building: false,
      door,
    });
  }

  /** The map's shade raster, or null on a map without one. A new map starts
   *  every actor in full colour until it next moves. */
  setShade(raster: ShadeRaster | null): void {
    this.shadeRaster = raster;
    this.shades.clear();
  }

  /** The ground items the GL pass drew in their drop models this frame, which
   *  `drawLootAt` gives no glyph (`PythonItem.cpp:121-132`). */
  setDropModelIds(ids: ReadonlySet<number>): void {
    this.dropModelIds = ids;
  }

  /** The guild insignias the GL pass drew in their own model this frame, which
   *  `drawGuildBuilding` gives no schematic (`InstanceBase.cpp:832-839`). */
  setInsigniaModelIds(ids: ReadonlySet<number>): void {
    this.insigniaModelIds = ids;
  }

  /** `CInstanceBase::Update`'s `SetMaterialColor` (`InstanceBase.cpp:1875-1893`):
   *  resampled from the ground only while the actor moves, and kept as it was
   *  while it stands. */
  private shadeFor(entityId: number, xMm: number, yMm: number, gait: Gait): readonly [number, number, number] {
    const raster = this.shadeRaster;
    if (raster === null || raster === undefined) return FULL_SHADE;
    const known = this.shades.get(entityId);
    if (known !== undefined && gait === Gait.IDLE) return known;
    const shade = shadeAt(raster, xMm, yMm);
    this.shades.set(entityId, shade);
    return shade;
  }

  /** Hand over the sampled palette once it has loaded. */
  setPalettes(palettes: Readonly<Record<string, CreaturePalette>>): void {
    this.palettes = palettes;
  }

  /** See `SceneRenderer.weaponAnchor`: the bone's world position from the
   *  last draw, facing the way the body was last seen facing. */
  weaponAnchor(entityId: number): { xMm: number; yMm: number; zMm: number; headingRad: number } | null {
    const anchor = this.weaponAnchors.get(entityId);
    if (anchor === undefined) return null;
    return { ...anchor, headingRad: headingToRadians(this.lastSeen.get(entityId)?.heading ?? 0) };
  }

  /** The off-hand copy of `weaponAnchor`, for the dagger's second glow. Null
   *  for every body but one that drew a weapon in both hands this frame. */
  weaponAnchorLeft(entityId: number): { xMm: number; yMm: number; zMm: number; headingRad: number } | null {
    const anchor = this.weaponAnchorsLeft.get(entityId);
    if (anchor === undefined) return null;
    return { ...anchor, headingRad: headingToRadians(this.lastSeen.get(entityId)?.heading ?? 0) };
  }

  headAnchor(entityId: number, mountVnum = 0): { xMm: number; yMm: number; zMm: number } | null {
    const seen = this.lastSeen.get(entityId);
    if (seen === undefined) return null;
    const meshHeightMm = this.meshes.status(seen.key).kind === "ready"
      ? this.meshes.heightMm(seen.key)
      : null;
    return {
      xMm: seen.xMm,
      yMm: seen.yMm,
      zMm: this.heightField.at(seen.xMm, seen.yMm)
        + this.mountLiftMm(mountVnum)
        + (meshHeightMm !== null && meshHeightMm > 0 ? meshHeightMm : FALLBACK_BODY_HEIGHT_MM),
    };
  }

  renderedCharacterParts(entityId: number): RenderedCharacterParts | null {
    return this.renderedCharacters?.get(entityId) ?? null;
  }

  /** Where the GM mark was actually painted for this entity, in CSS px, on the
   *  most recent frame that drew one -- null when this entity has no mark
   *  drawn (gm level 0, not currently on screen, or the sprite has not
   *  decoded yet). See `markRects`. */
  markRect(entityId: number): { x: number; y: number; width: number; height: number } | null {
    return this.markRects.get(entityId) ?? null;
  }

  weaponAttackClip(vnum: number): ClipName {
    return this.meshes.weaponAttackClip(vnum);
  }

  /** The clip `entityId`'s body is playing now, or null for one not drawn. */
  activeClip(entityId: number): ClipName | null {
    return this.meshes.activeClip(entityId);
  }

  /** The pack row of the clip `entityId`'s body is playing, under the model it
   *  was last drawn as, or null for one not drawn. */
  activeClipEntry(entityId: number): ClipEntry | null {
    const clip = this.meshes.activeClip(entityId);
    const seen = this.lastSeen.get(entityId);
    return clip === null || seen === undefined ? null : this.meshes.clipEntry(seen.key, clip);
  }

  attackClipEntry(armourVnum: number, race: number, clip: ClipName): ClipEntry | null {
    return this.meshes.clipEntry(this.bodyFor(armourVnum, race), clip);
  }

  attackClipDurationMs(armourVnum: number, race: number, clip: ClipName): number {
    return this.meshes.clipDurationMs(this.bodyFor(armourVnum, race), clip);
  }

  comboChainGapMsFor(armourVnum: number, race: number, clip: ClipName): number | null {
    return comboChainGapMs(this.meshes.clipEntry(this.bodyFor(armourVnum, race), clip));
  }

  /** The steps `race` plays from `clip` as combo type `comboType`, from
   *  `attackdata.json`'s combos once it has loaded -- see `comboChainFor`. */
  comboChainFor(race: number, clip: ClipName, comboType: number): readonly ClipName[] | null {
    return comboChainFor(clip, comboType, this.attackData?.combos[String(race)]);
  }

  attackHitStartMs(armourVnum: number, race: number, clip: ClipName): number | null {
    const entry = this.meshes.clipEntry(this.bodyFor(armourVnum, race), clip);
    return entry?.hitStartS === undefined ? null : entry.hitStartS * 1000;
  }

  /** How long a body casting `clip` keeps its target: the clip's whole length
   *  where it carries a fly, warp or effect-to-target event, which
   *  `CanChangeTarget` refuses to leave (`ActorInstanceFly.cpp:111-116`); null
   *  for every other motion. A rider holding `mountedWeapon` plays the file his
   *  mounted mode resolves (`mountedModeFor`, `riderSkillClip`). */
  skillClipHoldMs(armourVnum: number, race: number, clip: ClipName, mountedWeapon: number | null = null): number | null {
    const body = this.bodyFor(armourVnum, race);
    const played = mountedWeapon === null
      ? clip
      : riderSkillClip(clip, mountedModeFor(this.meshes, mountedWeapon), (named) => this.meshes.clipEntry(body, named) !== null);
    const entry = played === null ? null : this.meshes.clipEntry(body, played);
    return entry !== null && needsFlyTarget(entry) ? oneShotLengthS(entry) * 1000 : null;
  }

  /** Whether this weapon vnum is drawn as a bow -- a left-hand-only signature
   *  (`GameLib/ActorInstanceAttach.cpp:104-122`, and `looksLikeBow`). An
   *  archer's blow lands when the arrow arrives, not at a frame of the draw
   *  motion, so `main.ts` arms no local hit frame for one. */
  attackIsRanged(weaponVnum: number): boolean {
    return looksLikeBow(this.meshes.itemHands(weaponVnum));
  }

  /** How long a held attack key waits after `clip` before the next swing --
   *  see `comboSwingIntervalMs`, which is the original's whole melee cadence.
   *  Null when this pack has no `ComboInputData` for the clip. */
  comboSwingIntervalMsFor(
    armourVnum: number, race: number, clip: ClipName, attackSpeed: number,
  ): number | null {
    return comboSwingIntervalMs(
      this.meshes.clipEntry(this.bodyFor(armourVnum, race), clip), attackSpeed);
  }

  private paletteFor(name: string): CreaturePalette {
    return this.palettes[name] ?? DEFAULT_PALETTE;
  }

  private groundProject(xMm: number, yMm: number): Projected {
    return this.camera.project(xMm, yMm, this.heightField.at(xMm, yMm));
  }

  /** Project a Canvas2D fallback at the same elevation as a mesh body. A
   *  fallback rider still has to sit on a visible mount: the mesh path adds
   *  `liftMm` in its model matrix, while a screen-space silhouette otherwise
   *  stays at the ground projection and is drawn inside the horse. */
  private fallbackProject(
    xMm: number,
    yMm: number,
    ground: Projected,
    liftMm: number,
  ): Projected {
    if (liftMm <= 0) return ground;
    const lifted = this.camera.project(
      xMm,
      yMm,
      this.heightField.at(xMm, yMm) + liftMm,
    );
    return lifted.behind ? ground : lifted;
  }

  draw(frame: RenderFrame): void {
    // Cleared every frame regardless of whether anything draws as geometry
    // this time -- a stale creature left over from last frame is worse than
    // one metre of empty GPU work paying to make sure that never happens.
    this.meshes.beginFrame(this.ctx.canvas.width, this.ctx.canvas.height);
    this.renderedFrame = (this.renderedFrame ?? 0) + 1;
    this.traceNowMs = frame.nowMs;
    (this.renderedCharacters ??= new Map()).clear();

    const jobs: Array<{ readonly depth: number; readonly run: () => void }> = [];

    // Who is on screen this frame, decided before anything is drawn rather
    // than after: three things below ask it -- whether a corpse still has a
    // living entity drawing it, which entities can flinch, and which
    // per-entity tables to forget at the end.
    this.live.clear();
    this.live.add(frame.playerId);
    for (const other of frame.others) this.live.add(other.id);
    // Before the viewport cull: a motion event fires on its own clock, drawn
    // or not (`ActorInstanceMotionEvent.cpp:12-35`).
    this.stoneSpawns.frame(
      this.effects, frame.others, frame.nowMs, (body, bone) => this.spawnMotionAnchor(body, bone),
      (body, nowMs) => this.meshes.playOnce(body.id, meshKeyFor(this.meshes, body), "spawn", nowMs),
    );

    // Combat animations are started before anything is drawn, so that the
    // body about to be painted this frame is already in the right clip.
    this.startCombatClips(frame);
    this.startActClips(frame);
    this.stepScreenWaves(frame);
    this.drawCombatDecals(frame);

    for (const corpse of this.corpses.values()) {
      if (!corpseLies(corpse, this.live.has(corpse.entityId), frame.nowMs)) continue;
      const proj = this.groundProject(corpse.xMm, corpse.yMm);
      if (proj.behind) continue;
      jobs.push({ depth: proj.depth, run: () => this.drawCorpse(corpse) });
    }

    for (const mark of frame.combat.deaths) {
      if (deathPhase(mark, frame.nowMs) >= 1) continue;
      const proj = this.groundProject(mark.xMm, mark.yMm);
      if (proj.behind) continue;
      jobs.push({ depth: proj.depth, run: () => this.drawDeath(mark, frame.nowMs, proj) });
    }

    for (const other of frame.others) {
      // `__Assassin_SetEunhyeongAffect` sets alpha 0.0 for everyone but self
      // (`InstanceBaseEffect.cpp:805-813`). Skipping the draw is that 0.0:
      // a modified client that ignored the flag would still have the body,
      // matching the original 0-alpha instance remaining in the world.
      if (isStealthed(other)) continue;
      const proj = this.groundProject(other.xMm, other.yMm);
      // Behind the camera has no honest screen position -- see `View.project`
      // -- so it is skipped here rather than drawn mirrored through the centre
      // of the screen at the wrong size.
      if (proj.behind) continue;
      // Cheap cull: entities outside the viewport still arrive in snapshots at
      // the edge of interest range, and drawing text for each of them is not
      // free.
      if (
        proj.x < -64 ||
        proj.y < -64 ||
        proj.x > this.camera.viewportWidth + 64 ||
        proj.y > this.camera.viewportHeight + 64
      ) {
        continue;
      }
      jobs.push({ depth: proj.depth, run: () => this.drawOther(other, frame, proj) });
    }

    const ghost = frame.sitingGhost;
    if (ghost != null) {
      const ghostProj = this.groundProject(ghost.xMm, ghost.yMm);
      if (!ghostProj.behind) {
        const ghostEntity = {
          id: SITING_INSTANCE_ID, name: "", kind: EntityKind.BUILDING,
          xMm: ghost.xMm, yMm: ghost.yMm, heading: ghost.heading, buildingVnum: ghost.vnum, guildId: 0,
        } as unknown as RemoteEntity;
        jobs.push({
          depth: ghostProj.depth,
          run: () => this.drawGuildBuilding(ghostProj.x, ghostProj.y, ghostProj.scale, ghostEntity, undefined, ghost.alpha),
        });
      }
    }

    const playerProj = this.groundProject(frame.player.xMm, frame.player.yMm);
    if (!playerProj.behind) {
      jobs.push({ depth: playerProj.depth, run: () => this.drawPlayer(frame, playerProj) });
    }

    // Far to near: painted in this order, a nearer creature's pixels land on
    // top of a farther one's rather than the reverse. `depth` grows with
    // distance (see `View.project`), so descending is farthest first.
    jobs.sort((a, b) => b.depth - a.depth);
    for (const job of jobs) job.run();

    this.raiseDust(frame);

    // A gesture is an event over a living actor, not a body part. Paint it
    // after depth-sorted bodies so the readable mark remains above the actor
    // even when two characters overlap. The event came from `EmotePlayed`; no
    // local button press is allowed to create one.
    this.drawGestures(frame);

    // A melee trace belongs above the body that swung it and below combat
    // effects/floaters. Its event and timestamp are already authoritative in
    // `CombatView.swings`; this only turns the current held-weapon snapshot
    // into a readable presentation detail.
    this.drawWeaponTrails(frame);

    // The particle layer, between the creature meshes and this one when it has
    // a standalone canvas. Safari's GL path shares the terrain canvas instead,
    // so this call runs after the mesh pass without clearing the ground -- see
    // `EffectRenderer.draw` for the two attachment and clear policies.
    this.effects.drawFallbackArrows?.(this.ctx, this.camera, frame.nowMs);
    this.effects.drawFallbackLevelUps?.(this.ctx, this.camera, frame.nowMs);
    this.effects.draw(this.camera, frame.nowMs, this.ctx.canvas.width, this.ctx.canvas.height);

    if (shouldShowDamage(frame)) {
      for (const floater of frame.combat.floaters) this.drawFloater(floater, frame);
    }

    // Forget the gait of anything that stopped being drawn. Interest management
    // means entities come and go constantly, and a distance table that only
    // grows is a leak measured in hours rather than in megabytes.
    this.gait.prune(this.live);
    this.dust.prune(this.live);
    this.forgetTheDeparted(frame.nowMs);
  }

  private raiseDust(frame: RenderFrame): void {
    const walkers: DustWalker[] = [{
      id: frame.playerId,
      xMm: frame.player.xMm,
      yMm: frame.player.yMm,
      walking: frame.player.gait !== Gait.IDLE,
      isPlayerCharacter: true,
      mounted: (frame.playerMount ?? 0) !== 0,
    }];
    for (const other of frame.others) {
      walkers.push({
        id: other.id,
        xMm: other.xMm,
        yMm: other.yMm,
        walking: other.gait !== Gait.IDLE,
        isPlayerCharacter: other.kind === EntityKind.PLAYER,
        mounted: other.mount !== 0,
      });
    }
    for (const puff of this.dust.step(walkers, frame.player.xMm, frame.player.yMm)) {
      this.effects.spawn(puff.effect, {
        xMm: puff.xMm,
        yMm: puff.yMm,
        zMm: this.heightField.at(puff.xMm, puff.yMm),
      });
    }
  }

  private startCombatClips(frame: RenderFrame): void {
    for (const impact of frame.combat.impacts) {
      if (this.flinched.has(impact.key)) continue;
      this.flinched.add(impact.key);
      const seen = this.lastSeen.get(impact.entityId);
      if (seen === undefined) continue;
      // `AttackingProcess` refuses a victim inside its invisibility window before
      // the blow does anything to it (`ActorInstanceCollisionDetection.cpp:481-482`).
      if (this.isInvisible(impact.entityId, impact.bornMs)) continue;
      this.startInvisibility(impact.entityId, impact.attackerId, impact.skill ?? 0, impact.bornMs, frame.playerId);
      const reaction = this.reactionTo(impact.entityId, seen, impact.attackerId);
      if (reaction.clip === null) continue;
      // Only the client's own character flinches out of a walk or a run;
      // see `CreatureMeshRenderer.playOnce`'s `isMain` for the original's
      // `m_isMain` gate this mirrors.
      this.meshes.playOnce(
        impact.entityId, seen.key, reaction.clip, impact.bornMs, impact.entityId === frame.playerId, 1, reaction.then,
      );
    }

    // The same three events, as particles. Kept in their own loop rather than
    // folded into the flinch loop above because they answer a different
    // question -- a flinch needs a mesh instance to play a clip on and is
    // skipped without one, while a burst of sparks only needs a place to be.
    this.startCombatEffects(frame);

    for (const swing of frame.combat.swings) {
      if (this.swung.has(swing.key)) continue;
      this.swung.add(swing.key);
      const seen = this.lastSeen.get(swing.entityId);
      // Nothing drawn for the attacker means it is outside what this client is
      // sent -- a party member fighting off-screen. The log line still lands.
      if (seen === undefined) continue;
      const previous = this.comboStep.get(swing.entityId);
      const previousClip = previous === undefined
        ? null
        : this.meshes.clipEntry(seen.key, previous.clip ?? nextComboClip(seen.attackClip, previous.step));
      const next = swing.step !== undefined
        ? { step: swing.step, lastMs: swing.bornMs }
        : advanceComboStep(previous, swing.bornMs, comboChainGapMs(previousClip) ?? COMBO_CHAIN_GAP_MS);
      const swingClip = swing.clip ?? (swing.motion !== undefined
        ? comboMotionClip(seen.attackClip, swing.motion)
        : nextComboClip(seen.attackClip, next.step));
      this.comboStep.set(swing.entityId, { ...next, clip: swingClip });
      const intervalMs = previous === undefined ? 0 : swing.bornMs - previous.lastMs;
      const swingRunsForMs = this.meshes.playOnce(
        swing.entityId, seen.key, swingClip, swing.bornMs,
        swing.entityId === frame.playerId,
        swingPlaybackRatio(comboSwingIntervalMs(previousClip, 100), intervalMs),
      );
      this.waveFor(swing.entityId, seen.key, swingClip, swing.bornMs, swingRunsForMs);
    }

    if (frame.playerVitals.hp > 0) this.standUp(frame.playerId);
    for (const other of frame.others) if (other.hp > 0) this.standUp(other.id);

    for (const mark of frame.combat.deaths) {
      if (this.fallen.has(mark.key)) continue;
      this.fallen.add(mark.key);
      chatTails.delete(mark.entityId);
      const seen = this.lastSeen.get(mark.entityId);
      if (seen === undefined) continue;
      const runsForMs = this.meshes.playOnce(mark.entityId, seen.key, "dead", mark.bornMs);
      this.waveFor(mark.entityId, seen.key, "dead", mark.bornMs, runsForMs);
      if (runsForMs <= 0) continue;
      this.corpses.set(mark.entityId, {
        ...seen,
        entityId: mark.entityId,
        untilMs: mark.bornMs + Math.max(runsForMs, CORPSE_HOLD_MS),
        fellMs: mark.bornMs,
        kept: false,
      });
    }
  }

  /** The emotion, fishing and dig motions this frame's events push
   *  (`InstanceBaseMotion.cpp:49-88,162-165`, `InstanceBase.cpp:1831-1845`,
   *  `PythonNetworkStreamPhaseGame.cpp:4358-4359`). */
  private startActClips(frame: RenderFrame): void {
    const raceOf = (id: number): number => id === frame.playerId
      ? frame.playerRace ?? 0
      : frame.others.find((other) => other.id === id)?.race ?? 0;
    const mountOf = (id: number): number => id === frame.playerId
      ? frame.playerMount ?? 0
      : frame.others.find((other) => other.id === id)?.mount ?? 0;
    const weaponOf = (id: number): number => id === frame.playerId
      ? weaponVnumWorn(frame.playerWorn)
      : frame.others.find((other) => other.id === id)?.weapon ?? 0;
    const play = (id: number, clip: ClipName, bornMs: number, then: ClipName | null = null): void => {
      const seen = this.lastSeen.get(id);
      if (seen === undefined) return;
      playRiderMotion(
        this.meshes, id, seen.key, mountOf(id), clip, bornMs, id === frame.playerId, then,
        mountedModeFor(this.meshes, weaponOf(id)),
      );
    };
    for (const mark of frame.gestures ?? []) {
      if (this.gestured.has(mark.key)) continue;
      this.gestured.add(mark.key);
      const clips = emoteClips(mark.emote, raceOf(mark.actorId), raceOf(mark.targetId));
      if (clips === null) continue;
      play(mark.actorId, clips.actor, mark.bornMs);
      if (clips.target !== null && mark.targetId !== 0) play(mark.targetId, clips.target, mark.bornMs);
    }
    for (const mark of frame.motions ?? []) {
      if (this.moved.has(mark.key)) continue;
      this.moved.add(mark.key);
      play(mark.entityId, mark.clip, mark.bornMs, mark.then ?? null);
    }
    if (frame.playerMotionMode === "digging" && this.meshes.activeClip(frame.playerId) !== "dig") {
      play(frame.playerId, "dig", frame.nowMs);
    }
    // `RecvDigMotionPacket` pushes the dig on whichever body the packet names
    // (`PythonNetworkStreamPhaseGame.cpp:4350-4359`); a bystander learns of it
    // from `ENTITY_MINING`.
    for (const other of frame.others) {
      if (isMining(other) && this.meshes.activeClip(other.id) !== "dig") play(other.id, "dig", frame.nowMs);
    }
  }

  /** Draw the fading, player-visible ribbon for each recent melee swing. */
  /** `TraceProcess` (`ActorInstanceWeaponTrace.cpp:5-17`) for one character
   *  just drawn: each hand's trace samples the blade as that draw posed it while
   *  the playing motion is one that can attack (`ActorInstanceMotion.cpp:735-746`),
   *  and a bow carries none (`ActorInstanceAttach.cpp:124-137`). */
  private sampleWeaponTraces(
    entityId: number,
    body: string,
    held: Readonly<Record<string, string>> | null,
    anchor: WeaponTrailPoint | null,
    anchorLeft: WeaponTrailPoint | null,
  ): void {
    const traces = (this.weaponTraces ??= new Map());
    if (held === null || looksLikeBow(Object.keys(held))) {
      traces.delete(entityId);
      return;
    }
    let pair = traces.get(entityId);
    if (pair === undefined) {
      pair = { right: new WeaponTrace(), left: new WeaponTrace() };
      traces.set(entityId, pair);
    }
    const attacking = (this.meshes.hitWindowMs?.(entityId, body) ?? null) !== null;
    const tip = this.meshes.weaponTip?.() ?? null;
    const tipLeft = this.meshes.weaponTipLeft?.() ?? null;
    const nowMs = this.traceNowMs ?? 0;
    pair.right.update(nowMs, attacking && anchor !== null && tip !== null ? { hilt: anchor, tip } : null);
    pair.left.update(nowMs, attacking && anchorLeft !== null && tipLeft !== null ? { hilt: anchorLeft, tip: tipLeft } : null);
  }

  /** `RenderTrace` (`ActorInstanceWeaponTrace.cpp:19-22`) for every character
   *  still standing. */
  private drawWeaponTrails(_frame: RenderFrame): void {
    const traces = this.weaponTraces;
    if (traces === undefined) return;
    const project = (point: WeaponTrailPoint) => {
      const projected = this.camera.project(point.xMm, point.yMm, point.zMm);
      return projected.behind ? null : { x: projected.x, y: projected.y };
    };
    for (const [id, pair] of traces) {
      if (!this.live.has(id)) {
        traces.delete(id);
        continue;
      }
      drawWeaponTrail(this.ctx, weaponTraceStrip(pair.right), project);
      drawWeaponTrail(this.ctx, weaponTraceStrip(pair.left), project);
    }
  }

  /** Paint recent landed-hit footprints before the living bodies. */
  private drawCombatDecals(frame: RenderFrame): void {
    for (const impact of frame.combat.impacts) {
      if (!hasCombatDecal(impact)) continue;
      const alpha = combatDecalAlpha(impact.bornMs, frame.nowMs);
      if (alpha <= 0) continue;
      const path = combatDecalPath(
        impact.xMm,
        impact.yMm,
        this.heightField.at(impact.xMm, impact.yMm),
      );
      drawCombatDecal(
        this.ctx,
        path,
        (point) => {
          const projected = this.camera.project(point.xMm, point.yMm, point.zMm);
          return projected.behind ? null : { x: projected.x, y: projected.y };
        },
        impact.style,
        alpha,
      );
    }
  }

  /** Paint the browser fallback for the legacy motion clips. */
  private drawGestures(frame: RenderFrame): void {
    for (const mark of frame.gestures) {
      if (gestureVisual(mark, frame.nowMs).alpha <= 0) continue;
      let xMm: number;
      let yMm: number;
      let bodyKey: string | null = null;
      let mount = 0;
      let radiusMm = PLAYER_RADIUS_MM;
      if (mark.actorId === frame.playerId) {
        xMm = frame.player.xMm;
        yMm = frame.player.yMm;
        bodyKey = this.bodyFor(
          bodyVnumWorn(frame.playerWorn),
          frame.playerRace ?? 0,
        );
        mount = frame.playerMount ?? 0;
      } else {
        const actor = frame.others.find((entity) => entity.id === mark.actorId);
        if (actor === undefined) continue;
        xMm = actor.xMm;
        yMm = actor.yMm;
        bodyKey = this.meshKeyFor(actor);
        mount = actor.mount;
        if (actor.kind === EntityKind.MONSTER) radiusMm = MONSTER_RADIUS_MM;
        else if (actor.kind === EntityKind.STONE) radiusMm = STONE_RADIUS_MM;
      }
      const projected = this.groundProject(xMm, yMm);
      if (
        projected.behind ||
        projected.x < -64 ||
        projected.y < -64 ||
        projected.x > this.camera.viewportWidth + 64 ||
        projected.y > this.camera.viewportHeight + 64
      ) continue;

      const meshHeightMm = bodyKey === null || this.meshes.status(bodyKey).kind !== "ready"
        ? null
        : this.meshes.heightMm(bodyKey);
      const modelTop = meshHeightMm === null
        ? null
        : this.camera.project(
            xMm,
            yMm,
            this.heightField.at(xMm, yMm) + this.mountLiftMm(mount) + meshHeightMm,
          );
      const gestureY = gestureAnchorY(
        projected.y,
        Math.max(3, radiusMm * projected.scale),
        projected.scale,
        modelTop?.behind === false ? modelTop.y : null,
      );
      const partnered = mark.targetId !== 0;
      drawGesture(
        this.ctx,
        projected.x,
        gestureY,
        gestureVisual(mark, frame.nowMs),
        partnered,
      );
    }
  }

  private startCombatEffects(frame: RenderFrame): void {
    const groundAt = (xMm: number, yMm: number): number => this.heightField.at(xMm, yMm);

    for (const floater of frame.combat.floaters) {
      if (this.loosed.has(floater.key)) continue;
      this.loosed.add(floater.key);
      const struck = this.lastSeen.get(floater.entityId);
      const headingRad = headingToRadians(struck?.heading ?? 0);
      // Keyed by the floater's own key, which `combat.ts` allocates once per
      // event: the same shot offered on a second frame cannot be loosed
      // twice, and neither can the burst it is carrying.
      const shot = arrowShotFor(
        this.lastSeen.get(floater.attackerId),
        floater,
        headingRad,
        groundAt,
      );
      if (shot === null) continue;
      this.effects.launchArrow(
        String(floater.key), shot.from, shot.to, floater.bornMs, shot.impact);
    }

    for (const impact of frame.combat.impacts) {
      if (this.sparked.has(impact.key)) continue;
      this.sparked.add(impact.key);
      const archer = this.lastSeen.get(impact.attackerId);
      if (archer?.held != null && looksLikeBow(Object.keys(archer.held))) continue;
      const effect = hitEffectFor(impact.style);
      if (effect === null) continue;
      const struck = this.lastSeen.get(impact.entityId);
      this.effects.spawn(effect, {
        xMm: impact.xMm,
        yMm: impact.yMm,
        zMm: groundAt(impact.xMm, impact.yMm),
        headingRad: headingToRadians(struck?.heading ?? 0),
      });
    }

    for (const mark of frame.combat.deaths) {
      if (this.shattered.has(mark.key)) continue;
      this.shattered.add(mark.key);
      // Each plays at the size the original authored it at, unscaled.
      const kind = this.lastSeen.get(mark.entityId)?.kind;
      const at = { xMm: mark.xMm, yMm: mark.yMm, zMm: this.heightField.at(mark.xMm, mark.yMm) };
      for (const event of deathEffectsFor(kind)) {
        this.dueEffects.push({ atMs: frame.nowMs + event.atS * 1000, effect: event.effect, at });
      }
    }
    this.playDueEffects(frame.nowMs);

    for (const cast of frame.combat.casts) {
      if (this.castKeys.has(cast.key)) continue;
      this.castKeys.add(cast.key);
      const key = this.effects.effectForSkill(cast.vnum);
      if (key === null) continue;
      // The caster's live position if he is still being drawn, so a self-buff
      // sits on him rather than a step behind; the recorded one otherwise.
      const seen = this.lastSeen.get(cast.casterId);
      const xMm = seen?.xMm ?? cast.xMm;
      const yMm = seen?.yMm ?? cast.yMm;
      this.effects.spawn(key, {
        xMm,
        yMm,
        zMm: this.heightField.at(xMm, yMm),
        headingRad: seen === undefined ? cast.headingRad : headingToRadians(seen.heading),
      });
    }

    for (const mark of [...frame.combat.specials, ...frame.combat.points]) {
      if (this.specialKeys.has(mark.key)) continue;
      this.specialKeys.add(mark.key);
      const key =
        "effect" in mark ? this.effects.effectForSpecial(mark.effect) : this.effects.effectForPoint(mark.point);
      if (key === null) continue;
      const seen = this.lastSeen.get(mark.entityId);
      if (seen === undefined) continue;
      this.attachRegistered(key, mark.entityId, seen);
    }

    for (const mark of frame.combat.stuns) {
      if (this.specialKeys.has(mark.key)) continue;
      this.specialKeys.add(mark.key);
      const seen = this.lastSeen.get(mark.entityId);
      if (seen === undefined) continue;
      this.attachRegistered(stunEffect(), mark.entityId, seen);
    }
  }

  /** `__AttachEffect` (`InstanceBaseEffect.cpp:1029-1066`): `key` attached to
   *  the body, on the bone its registration names (`EFFECT_BONES`) or its root.
   *  A bone's follow is seeded at the root until the body's draw poses it. */
  private attachRegistered(key: string, entityId: number, seen: { xMm: number; yMm: number; heading: number }): void {
    const groundMm = this.heightField.at(seen.xMm, seen.yMm);
    const followId = effectFollowId(entityId, key);
    if (followId !== entityId && !this.wornBone(followId)) this.effects.follow(followId, seen.xMm, seen.yMm, groundMm);
    this.effects.spawn(key, {
      xMm: seen.xMm,
      yMm: seen.yMm,
      zMm: groundMm,
      headingRad: headingToRadians(seen.heading),
      followId,
    });
  }

  /** Spawn every queued death-motion effect whose time has come, once. */
  private playDueEffects(nowMs: number): void {
    const waiting: typeof this.dueEffects = [];
    for (const due of this.dueEffects) {
      if (nowMs >= due.atMs) this.effects.spawn(due.effect, due.at);
      else waiting.push(due);
    }
    this.dueEffects = waiting;
  }

  private forgetTheDeparted(nowMs: number): void {
    for (const [id, corpse] of this.corpses) {
      if (nowMs >= corpse.untilMs) this.corpses.delete(id);
    }
    for (const id of this.lastSeen.keys()) {
      if (!this.live.has(id) && !this.corpses.has(id)) {
        const attached = this.attachedEffects?.get(this.lastSeen.get(id)?.key ?? "") ?? [];
        this.lastSeen.delete(id);
        // The instance is gone, so what was hung on it goes with it:
        // `CInstanceBase::Destroy` clears the attaching effects rather than
        // leaving a looping affect burning over an empty patch of ground
        // (`m_GraphicThingInstance.ClearAttachingEffect()`).
        this.effects.unfollow(id);
        attached.forEach((_row, index) => this.effects.unfollow(attachedEffectFollowId(id, index)));
        for (const bone of EFFECT_ATTACH_BONES) this.effects.unfollow(effectBoneFollowId(id, bone));
        chatTails.delete(id);
      }
    }
    for (const id of this.alphaRamps.keys()) {
      if (!this.live.has(id) && !this.corpses.has(id)) this.alphaRamps.delete(id);
    }
    for (const id of this.weaponAnchors.keys()) {
      if (!this.live.has(id)) this.weaponAnchors.delete(id);
    }
    for (const id of this.shades.keys()) {
      if (this.live.has(id)) continue;
      this.shades.delete(id);
      this.meshes.forgetShade?.(id);
      this.meshes.forgetShade?.(-id);
    }
    for (const id of this.weaponAnchorsLeft.keys()) {
      if (!this.live.has(id)) this.weaponAnchorsLeft.delete(id);
    }
    for (const id of this.volumes?.keys() ?? []) {
      if (!this.live.has(id)) this.volumes.delete(id);
    }
    for (const id of this.invisibleUntilMs?.keys() ?? []) {
      if (!this.live.has(id)) this.invisibleUntilMs.delete(id);
    }
    // `comboStep` is keyed by entity id, not by an event key, so it is
    // liveness that prunes it: a corpse cannot be mid-chain and a departed
    // entity's id means nothing to a new arrival that reuses it later.
    for (const id of this.comboStep.keys()) {
      if (!this.live.has(id)) this.comboStep.delete(id);
    }
    forget(this.fallen, 64);
    forget(this.flinched, 512);
    forget(this.gestured, 64);
    forget(this.moved, 64);
    // Same three-line argument for the effect layer's own three tables: keys
    // strictly increase, the feeds they index are capped, so a key far enough
    // below the highest can never come round to fire an effect twice.
    forget(this.sparked, 512);
    forget(this.loosed, 512);
    forget(this.shattered, 64);
    forget(this.castKeys, 64);
    forget(this.specialKeys, 64);
  }

  /** The camera-target offset of the screen wave in force at `nowMs`. */
  screenWaveOffset(nowMs: number): WaveOffset {
    return this.screenWaves.offsetAt(nowMs);
  }

  /** `__isInvisible` (`ActorInstanceBattle.cpp:952-961`), past its `IsDead`
   *  arm: a dead body's motion is `playOnce`'s to refuse. */
  private isInvisible(entityId: number, nowMs: number): boolean {
    return nowMs < (this.invisibleUntilMs.get(entityId) ?? 0);
  }

  /** `__ProcessDataAttackSuccess`'s invisibility window (`ActorInstanceBattle.cpp:
   *  605-608,620-635`): a blow of any hit type but none makes its victim
   *  untouchable for the attacking clip's `InvisibleTime`, when the blow is a
   *  skill or the client's own -- `IS_PARTY_HUNTING_RACE` answers true for every
   *  race (`:541-556`). */
  private startInvisibility(victimId: number, attackerId: number, skill: number, nowMs: number, playerId: number): void {
    if (skill === 0 && attackerId !== playerId) return; // ActorInstanceBattle.cpp:623-631
    const attacker = this.lastSeen.get(attackerId);
    const swinging = attacker === undefined ? null : this.meshes.activeClip(attackerId);
    if (attacker === undefined || swinging === null || !isAttackClip(swinging)) return;
    if (this.meshes.clipEntry(attacker.key, swinging)?.hittingType === HIT_TYPE_NONE) return; // :607-608
    const blow = this.attackData === null ? null : attackDataFor(this.attackData, attacker.key, swinging);
    if (blow === null) return;
    this.invisibleUntilMs.set(victimId, nowMs + blow.invisibleTimeS * 1000); // :627,630
  }

  /** `RefreshActorInstance`'s body and defending rebuild whenever the body a
   *  species draws changes (`ActorInstanceAttach.cpp:302-491`), then
   *  `UpdatePointInstance` over the defending list, which is the only list it
   *  places each frame (`ActorInstanceCollisionDetection.cpp:26-31`). */
  private keepVolumes(entityId: number, key: string, xMm: number, yMm: number, zMm: number, heading: number): void {
    if (!this.collisionVolumes) return;
    let held = this.volumes.get(entityId);
    if (held === undefined || held.key !== key) {
      held = { key, kept: refreshActorVolumes(collisionRowsFor(this.collisionVolumes, key)), defending: [] };
      this.volumes.set(entityId, held);
    }
    held.defending = placeVolumes(
      held.kept.defending, { xMm, yMm, zMm, heading },
      (bone) => this.meshes.boneAnchor?.(key, bone) ?? null, held.defending,
    );
  }

  /** The defending spheres `entityId`'s last draw placed, or none for a body not
   *  drawn: what `IntersectDefendingSphere` tests (`ActorInstance.cpp:753-782`). */
  defendingSpheres(entityId: number): readonly PlacedSphere[] {
    return this.volumes.get(entityId)?.defending.flatMap((volume) => volume.spheres) ?? [];
  }

  /** `__ProcessDataAttackSuccess`'s motion for one landed blow: the attacker's
   *  playing clip's hit type against the victim (`ActorInstanceBattle.cpp:683-698`). */
  private reactionTo(victimId: number, victim: Seen, attackerId: number): HitReaction {
    const attacker = this.lastSeen.get(attackerId);
    const swinging = attacker === undefined ? null : this.meshes.activeClip(attackerId);
    const hittingType = attacker !== undefined && swinging !== null && isAttackClip(swinging)
      ? this.meshes.clipEntry(attacker.key, swinging)?.hittingType
      : undefined;
    const has: Partial<Record<ClipName, boolean>> = {};
    for (const clip of REACTION_CLIPS) has[clip] = this.meshes.clipEntry(victim.key, clip) !== null;
    const playing = this.meshes.activeClip(victimId);
    const motion = actorMotion(playing, playing === null ? null : this.meshes.clipEntry(victim.key, playing));
    return hitReaction({
      hittingType,
      attackerHeading: attacker?.heading ?? victim.heading,
      victimHeading: victim.heading,
      playing: playing ?? "idle",
      has,
      locked: isLock(motion),
      usingSkill: motion.usingSkill,
    });
  }

  /** Queue the screen waves of a one-shot `playOnce` really started. */
  private waveFor(entityId: number, key: string, clip: ClipName, bornMs: number, runsForMs: number): void {
    const entry = this.meshes.clipEntry(key, clip);
    if (entry === null || entry.screenWaving === undefined || runsForMs <= 0) return;
    const playing = this.meshes.activeClip(entityId);
    if (playing === null || this.meshes.clipEntry(key, playing) !== entry) return;
    this.screenWaves.schedule(entityId, bornMs, entry.screenWaving, (entry.durationS * 1000) / runsForMs);
  }

  /** Fire due waves against the main character, `CGameEventManager`'s centre
   *  (`GameEventManager.cpp:4-9,15-27`). */
  private stepScreenWaves(frame: RenderFrame): void {
    const at = (xMm: number, yMm: number): WaveOffset => ({ xMm, yMm, zMm: this.heightField.at(xMm, yMm) });
    this.screenWaves.step(frame.nowMs, (entityId) => {
      const body = entityId === frame.playerId ? frame.player : this.lastSeen.get(entityId);
      return body === undefined ? null : at(body.xMm, body.yMm);
    }, at(frame.player.xMm, frame.player.yMm));
  }

  private standUp(entityId: number): void {
    if (!this.corpses.has(entityId)) return;
    this.corpses.delete(entityId);
    this.meshes.revive(entityId);
  }

  private remember(
    entityId: number,
    key: string,
    xMm: number,
    yMm: number,
    heading: number,
    kind: number,
    palette: CreaturePalette,
    held: Readonly<Record<string, string>> | null = null,
    attackClip: ClipName = "attack",
    mobColor = 0,
  ): void {
    this.lastSeen.set(entityId, { key, xMm, yMm, heading, kind, palette, held, attackClip, mobColor });
    const groundMm = this.heightField.at(xMm, yMm);
    this.effects.follow(entityId, xMm, yMm, groundMm);
    // A bone the draw below does not pose is `FindBoneIndex` failing, and the
    // effect rides the root (`ActorInstanceAttach.cpp:277-295`).
    for (const bone of EFFECT_ATTACH_BONES) {
      const id = effectBoneFollowId(entityId, bone);
      if (this.wornBone(id)) this.effects.follow(id, xMm, yMm, groundMm);
    }
    if (kind === EntityKind.STONE) this.wearStoneSmoke(entityId, key, xMm, yMm, groundMm, heading);
    this.wearAttachedEffects(entityId, key, xMm, yMm, groundMm, heading);
  }

  /** `SetRace`'s attaching loop (`ActorInstanceData.cpp:247-266`): every
   *  `AttachingDataType 2` row of the species' `.msm` is `AttachEffectByName`
   *  on its bone, or on the root when it names none (`ActorInstanceAttach.cpp:
   *  258-300`), for as long as the body stands, and leaves with it. */
  private wearAttachedEffects(
    entityId: number,
    key: string,
    xMm: number,
    yMm: number,
    groundMm: number,
    heading: number,
  ): void {
    const rows = this.attachedEffects.get(key);
    if (rows === undefined || rows.length === 0) return;
    const followIdOf = (row: AttachedEffectRow, index: number): number =>
      row.bone === null ? entityId : attachedEffectFollowId(entityId, index);
    if (this.effects.isFollowing(followIdOf(rows[0], 0), rows[0].effect)) return;
    rows.forEach((row, index) => {
      if (!this.effects.has(row.effect)) return;
      this.effects.spawn(row.effect, {
        ...stoneSmokeAnchor(xMm, yMm, groundMm, heading, row.offsetMm),
        followId: followIdOf(row, index),
      });
    });
  }

  /** `UpdateAttachingInstances` (`ActorInstanceAttach.cpp:519-566`): each boned
   *  effect re-read onto its bone's position and axes as the body's `draw` just
   *  posed it, or onto the bone's bind offset and the body's axes, turned by its
   *  current heading, where that draw skinned no skeleton. A root row rides the
   *  body's own follow. */
  private followAttachedEffects(
    entityId: number,
    key: string,
    xMm: number,
    yMm: number,
    groundMm: number,
    heading: number,
  ): void {
    const rows = this.attachedEffects?.get(key);
    if (rows === undefined) return;
    rows.forEach((row, index) => {
      if (row.bone === null) return;
      const id = attachedEffectFollowId(entityId, index);
      const posed = this.meshes.boneAnchor?.(key, row.bone) ?? null;
      if (posed !== null) {
        this.effects.follow(id, posed.xMm, posed.yMm, posed.zMm, posed.basis);
        return;
      }
      const at = stoneSmokeAnchor(xMm, yMm, groundMm, heading, row.offsetMm);
      const m = creatureModelMatrix(0, 0, 0, heading);
      this.effects.follow(id, at.xMm, at.yMm, at.zMm, [m[0], m[1], m[2], m[4], m[5], m[6], m[8], m[9], m[10]]);
    });
  }

  /** Whether an effect rides this bone follow id; a renderer without an
   *  effect layer hangs nothing on any bone. */
  private wornBone(followId: number): boolean {
    return this.effects?.following?.(followId) === true;
  }

  /** `UpdateAttachingInstances` (`ActorInstanceAttach.cpp:519-566`) for the
   *  registered effects: each bone an effect rides (`EFFECT_BONES`) re-read
   *  onto its pose as the body's `draw` just left it. */
  private followEffectBones(entityId: number, key: string): void {
    for (const bone of EFFECT_ATTACH_BONES) {
      const id = effectBoneFollowId(entityId, bone);
      if (!this.wornBone(id)) continue;
      const posed = this.meshes.boneAnchor?.(key, bone) ?? null;
      if (posed !== null) this.effects.follow(id, posed.xMm, posed.yMm, posed.zMm, posed.basis);
    }
  }

  /** Where a stone's SPAWN-motion event attaches: the smoke bone when the
   *  event names the bone the stone's smoke row carries, the stone's root on
   *  the ground otherwise. */
  private spawnMotionAnchor(
    body: RemoteEntity,
    bone: string | null,
  ): { xMm: number; yMm: number; zMm: number } {
    const groundMm = this.heightField.at(body.xMm, body.yMm);
    const row = bone === null ? undefined : this.stoneSmoke.get(this.meshKeyFor(body));
    if (row === undefined || row.bone !== bone) return { xMm: body.xMm, yMm: body.yMm, zMm: groundMm };
    return stoneSmokeAnchor(body.xMm, body.yMm, groundMm, body.heading, row.offsetMm);
  }

  /** `CInstanceBase::__StoneSmoke_Create` with tier 0 (`InstanceBaseEffect.cpp:
   *  358-361,741-757`): no server this client talks to sets a stone-smoke
   *  affect bit, so every stone wears its first `SmokeFileName` loop on its
   *  smoke bone for as long as it stands, and the loop leaves with it. */
  private wearStoneSmoke(
    entityId: number,
    key: string,
    xMm: number,
    yMm: number,
    groundMm: number,
    heading: number,
  ): void {
    const row = this.stoneSmoke.get(key);
    if (row === undefined || !this.effects.has(row.effect)) return;
    if (this.effects.isFollowing(entityId, row.effect)) return;
    this.effects.spawn(row.effect, {
      ...stoneSmokeAnchor(xMm, yMm, groundMm, heading, row.offsetMm),
      followId: entityId,
    });
  }

  /** One body, drawn where it fell, playing the death clip it started while
   *  it was still an entity. No bar and no nameplate: it is not a thing that
   *  can be targeted any more. */
  private drawCorpse(corpse: Corpse): void {
    this.drawBody(
      corpse.key,
      corpse.entityId,
      corpse.xMm,
      corpse.yMm,
      corpse.heading,
      Gait.IDLE,
      corpse.palette,
      () => {},
      corpse.held,
      0,
      false,
      0,
      1,
      corpse.mobColor,
    );
  }

  private drawPlayer(frame: RenderFrame, proj: Projected): void {
    const state = frame.player;
    const shake = actorShakeOffset(frame.playerId, frame.combat.impacts, frame.nowMs);
    const renderXMm = state.xMm + shake.xMm;
    const renderYMm = state.yMm + shake.yMm;
    const renderProj = shake.active ? this.groundProject(renderXMm, renderYMm) : proj;
    const x = renderProj.x;
    const y = renderProj.y;
    // `scale` is pixels per millimetre at this entity's own depth, so a player
    // standing further from the camera than usual (the eased camera trails a
    // sprint by a couple of metres) draws at the size that depth implies rather
    // than at a fixed size a tilted camera can no longer promise.
    const radius = Math.max(4, PLAYER_RADIUS_MM * renderProj.scale);

    const selfFrame: CreatureFrame = {
      x,
      y,
      radius: radius * 1.5,
      angle: this.screenAngle(state.heading),
      distanceMm: this.gait.advance(frame.playerId, state.xMm, state.yMm),
      resting: state.gait === Gait.IDLE,
      palette: PLAYER_PALETTE,
    };
    const bodyKey = this.bodyFor(
      bodyVnumWorn(frame.playerWorn),
      frame.playerRace ?? 0,
    );
    const weaponVnum = weaponVnumWorn(frame.playerWorn);
    const dressed = this.meshes.armourShape?.(bodyVnumWorn(frame.playerWorn)) === WEDDING_DRESS_SHAPE;
    const held = this.heldFor(weaponVnum, (frame.playerMount ?? 0) !== 0, dressed);
    this.meshes.setMotionMode?.(
      frame.playerId,
      riderMotionMode(dressed, (frame.playerMount ?? 0) !== 0, frame.playerMotionMode ?? "general"),
    );
    this.remember(
      frame.playerId, bodyKey, state.xMm, state.yMm, state.heading,
      EntityKind.PLAYER, PLAYER_PALETTE, held, this.meshes.weaponAttackClip(weaponVnum),
    );
    // A rider sees his own horse too. Same call, same table, same lift: the
    // alternative is the one character in the world who disagrees with
    // everybody else about whether he is mounted.
    const saddle = this.drawMount(
      {
        id: frame.playerId,
        mount: frame.playerMount ?? 0,
        xMm: renderXMm,
        yMm: renderYMm,
        heading: state.heading,
        gait: state.gait,
      },
      x,
      y,
      renderProj.scale,
      this.castsShadowAt(
        frame, renderXMm, renderYMm,
        this.bodyAlpha(frame.playerId, frame.nowMs, isStealthed({ flags: frame.playerFlags ?? 0 })),
      ),
    );
    // A motion's hide event takes the body to alpha 0 until its show event
    // (`ActorInstanceMotionEvent.cpp:84-96`).
    const playerAlpha = this.meshes.isHiding?.(frame.playerId, bodyKey, frame.nowMs) === true ? 0 : this.bodyAlpha(
      frame.playerId, frame.nowMs, isStealthed({ flags: frame.playerFlags ?? 0 }),
    );
    const playerCasts = this.castsShadowAt(frame, renderXMm, renderYMm, playerAlpha);
    const bodyDrawn = this.drawBody(
      bodyKey,
      frame.playerId,
      renderXMm,
      renderYMm,
      state.heading,
      state.gait,
      PLAYER_PALETTE,
      (liftMm: number): void => {
        const lifted = this.fallbackProject(renderXMm, renderYMm, renderProj, liftMm);
        drawCreature(this.ctx, "humanoid", {
          ...selfFrame,
          x: lifted.x,
          y: lifted.y,
          radius: Math.max(4, PLAYER_RADIUS_MM * lifted.scale) * 1.5,
        });
      },
      held,
      saddle.liftMm,
      saddle.fallbackOverlay,
      shake.zMm,
      playerAlpha,
      0,
      playerCasts,
    );
    if (bodyDrawn) {
      const hair = this.drawDefaultHair(
        frame.playerRace ?? 0,
        this.meshes.costumeHairShape(hairCostumeVnumWorn(frame.playerWorn)),
        frame.playerId, renderXMm, renderYMm,
        state.heading, state.gait, PLAYER_PALETTE, saddle.liftMm + shake.zMm, bodyKey,
        playerAlpha, playerCasts,
      );
      this.recordCharacterParts(
        frame.playerId,
        bodyKey,
        hair,
        held,
        this.meshes.weaponAttackClip(weaponVnum),
        hair === null ? 0 : this.hairBonesJustDrawn(),
      );
    }

    const flashIntensity = frame.combat.flashAt(frame.playerId, frame.nowMs);
    this.drawFlash(
      x,
      flashIntensity > 0
        ? this.flashY(bodyKey, renderXMm, renderYMm, y, radius, saddle.liftMm + shake.zMm)
        : y,
      radius,
      flashIntensity,
    );

    // The local player's bar is nearly twice the size of everyone else's. It is
    // the one number a player checks without looking away from what they are
    // fighting, so it has to be readable in peripheral vision.
    let top = this.labelTop(
      bodyKey, renderXMm, renderYMm, y, radius, saddle.liftMm + shake.zMm,
    ) - 6;
    if (hasHealth(frame.playerVitals)) {
      this.drawHealthBar(x, top, frame.playerVitals, PLAYER_BAR_WIDTH_PX, PLAYER_BAR_HEIGHT_PX);
      top -= PLAYER_BAR_HEIGHT_PX + 4;
    }
    const nameLift = this.drawChatTail(x, top - 2, chatTails.textFor(frame.playerId));
    const plateY = top - 2 - nameLift;
    // The guild line first, so the mark stacks on top of the whole label
    // rather than landing in the middle of it.
    const guildLift = this.drawGuildTail(
      x, plateY, frame.playerGuildName ?? "", frame.guildEmblems?.get(frame.playerGuildId ?? 0),
    );
    const markLift = this.drawGmMark(
      x, plateY - PLATE_HEIGHT_PX - guildLift, frame.playerGmLevel ?? 0, frame.playerId,
    );
    const title = alignmentTitleFor({ flags: frame.playerFlags ?? 0 });
    this.drawPlate(
      x,
      plateY,
      frame.playerName,
      nameColourFor(
        { flags: frame.playerFlags ?? 0 },
        ordinaryNameColour(
          EntityKind.PLAYER,
          ENTITY_COLOURS.name,
          frame.nameColorMode,
          frame.nameColorEmpire,
        ),
      ),
      title.text,
      title.colour,
      levelTail(frame.playerVitals.level),
    );
    this.drawStallSign(x, top - 20 - nameLift - guildLift - markLift, frame.playerId, frame);
  }

  private drawOther(entity: RemoteEntity, frame: RenderFrame, proj: Projected): void {
    const ctx = this.ctx;
    const shake = actorShakeOffset(entity.id, frame.combat.impacts, frame.nowMs);
    const renderXMm = entity.xMm + shake.xMm;
    const renderYMm = entity.yMm + shake.yMm;
    const renderProj = shake.active ? this.groundProject(renderXMm, renderYMm) : proj;
    const x = renderProj.x;
    const y = renderProj.y;

    if (entity.kind === EntityKind.ITEM) {
      this.drawLootAt(x, y, renderProj.scale, entity, frame);
      return;
    }
    if (entity.kind === EntityKind.BUILDING) {
      this.drawGuildBuilding(x, y, renderProj.scale, entity, frame.guildSymbols);
      return;
    }

    const monster = entity.kind === EntityKind.MONSTER;
    const stone = entity.kind === EntityKind.STONE;
    const beast = monster || isPet(entity) || isHorse(entity) || entity.polymorph !== "";
    // Monsters are drawn larger and squarer than players, and in a warm colour
    // against the cool blue of other characters. Silhouette first, colour second:
    // a colour-blind player still has to be able to tell what is hostile.
    const radius = Math.max(
      3,
      (stone ? STONE_RADIUS_MM : monster ? MONSTER_RADIUS_MM : PLAYER_RADIUS_MM) * renderProj.scale,
    );
    const colour = entityColour(entity);

    ctx.fillStyle = colour;
    const distanceMm = this.gait.advance(entity.id, entity.xMm, entity.yMm);
    // Every monster this map spawns is a quadruped -- dogs, wolves, boars,
    // bears and one tiger, per `mob_proto`'s own FOLDER column. Players and
    // NPCs are people. A stone is neither, and its fallback is not a creature
    // at all, which is the whole reason `drawBody` takes a closure.
    const palette = monster
      ? this.paletteFor(entity.name)
      : entity.polymorph !== ""
        // A transformed player is coloured as the creature they are wearing,
        // not as a player: the palette is keyed by species name and
        // `creatures.json` has an entry for anything the marble can name.
        ? this.paletteFor(entity.polymorph)
        : { body: colour, dark: shade(colour) };
    const fallback = stone
      ? (_liftMm: number): void => this.drawStone(x, y, radius, entity)
      : (liftMm: number): void => {
          const lifted = this.fallbackProject(renderXMm, renderYMm, renderProj, liftMm);
          const liftedRadius = Math.max(
            3,
            (stone ? STONE_RADIUS_MM : monster ? MONSTER_RADIUS_MM : PLAYER_RADIUS_MM) * lifted.scale,
          );
          drawCreature(this.ctx, beast ? "quadruped" : "humanoid", {
            x: lifted.x,
            y: lifted.y,
            radius: liftedRadius * 1.5,
            angle: this.screenAngle(entity.heading),
            distanceMm,
            resting: entity.gait === Gait.IDLE,
            palette,
          });
        };
    const key = this.meshKeyFor(entity);
    const mobColor = mobColorOf(this.mobColors, entity.kind, entity.mobVnum);
    const dressed = entity.kind === EntityKind.PLAYER
      && this.meshes.armourShape?.(entity.armour) === WEDDING_DRESS_SHAPE;
    const held = entity.kind === EntityKind.PLAYER
      ? this.heldFor(entity.weapon, entity.mount !== 0, dressed)
      : null;
    if (entity.kind === EntityKind.PLAYER) {
      this.meshes.setMotionMode?.(
        entity.id, remoteMotionMode(dressed, frame.anglers?.has(entity.id) ?? false, entity.mount !== 0),
      );
    }
    this.remember(
      entity.id, key, entity.xMm, entity.yMm, entity.heading, entity.kind, palette, held,
      entity.kind === EntityKind.PLAYER
        ? this.meshes.weaponAttackClip(entity.weapon)
        : "attack",
      mobColor,
    );
    // The horse first, then the rider on top of it. Before this the mount was
    // invisible to everybody but its rider, so a player at 7.4 m/s read as
    // somebody sprinting -- see `EntityDelta.mount`.
    const otherAlpha = this.meshes.isHiding?.(entity.id, key, frame.nowMs) === true
      ? 0
      : this.bodyAlpha(entity.id, frame.nowMs, false);
    const otherCasts = this.castsShadowAt(frame, renderXMm, renderYMm, otherAlpha, isDungeonDoor(entity));
    const saddle = this.drawMount(
      { ...entity, xMm: renderXMm, yMm: renderYMm },
      x,
      y,
      renderProj.scale,
      otherCasts,
    );
    const meshHeightMm = this.meshes.heightMm(key);
    const headZMm = this.heightField.at(renderXMm, renderYMm)
      + saddle.liftMm + shake.zMm
      + (meshHeightMm !== null && meshHeightMm > 0 ? meshHeightMm : FALLBACK_BODY_HEIGHT_MM);
    const eye = this.camera.eyeMm;
    const overlayClear = eye === undefined || lineOfSightClear(
      this.heightField,
      eye.xMm, eye.yMm, eye.zMm,
      renderXMm, renderYMm, headZMm,
    );
    const bodyDrawn = this.drawBody(
      key, entity.id, renderXMm, renderYMm, entity.heading, entity.gait, palette,
      overlayClear ? fallback : () => {}, held, saddle.liftMm, saddle.fallbackOverlay, shake.zMm,
      otherAlpha, mobColor, otherCasts,
    );
    if (bodyDrawn && entity.kind === EntityKind.PLAYER && entity.polymorph === "") {
      const hair = this.drawDefaultHair(
        entity.race, entity.hair, entity.id, renderXMm, renderYMm,
        entity.heading, entity.gait, palette, saddle.liftMm + shake.zMm, key,
        otherAlpha, otherCasts,
      );
      this.recordCharacterParts(
        entity.id,
        key,
        hair,
        held,
        this.meshes.weaponAttackClip(entity.weapon),
        hair === null ? 0 : this.hairBonesJustDrawn(),
      );
    }
    // GL meshes are already depth-tested against the hill. The overlay is
    // not: a bar drawn here would float over the nearer ground with no body
    // under it.
    if (!overlayClear) return;
    // A ready GL body is depth-tested against the hill. A missing mesh
    // still gets a silhouette (drawBody already ran `fallback`). Overlay
    // chrome uses `labelTop`, which must not sit at pack height over a
    // ground blob -- see `flashY`'s ready gate.
    const flashIntensity = frame.combat.flashAt(entity.id, frame.nowMs);
    this.drawFlash(
      x,
      flashIntensity > 0
        ? this.flashY(key, renderXMm, renderYMm, y, radius, saddle.liftMm + shake.zMm)
        : y,
      radius,
      flashIntensity,
    );
    if (entity.id === frame.targetId) this.drawTargetRing(x, y, radius);

    let top = this.labelTop(
      key, renderXMm, renderYMm, y, radius, saddle.liftMm + shake.zMm,
    ) - 5;
    if (hasHealth(entity)) {
      this.drawHealthBar(x, top, entity, BAR_WIDTH_PX, BAR_HEIGHT_PX);
      top -= BAR_HEIGHT_PX + 3;
    }
    const tail = chatTailFor(entity, chatTails.textFor(entity.id));
    const named = plateShown(entity, frame, tail !== null);
    const plate = named ? entity.name : "";
    // Drawn at the name's own baseline, which is where the original puts it: the
    // chat tail takes the nameplate's slot and the nameplate moves up by
    // `CHAT_TAIL_NAME_LIFT_PX` (`UserInterface/PythonTextTail.cpp:105-115`).
    const nameLift = this.drawChatTail(x, top, tail);
    const guildLift = named
      ? this.drawGuildTail(
        x, top - nameLift, guildNameFor(entity, frame.guildNames), frame.guildEmblems?.get(entity.guildId),
      )
      : 0;
    const markLift = this.drawGmMark(
      x,
      top - nameLift - (named ? PLATE_HEIGHT_PX : 0) - guildLift,
      entity.gmLevel,
      entity.id,
    );
    if (named) {
      const title = alignmentTitleFor(entity);
      this.drawPlate(
        x,
        top - nameLift,
        plate,
        nameColourFor(
          entity,
          ordinaryNameColour(
            entity.kind,
            entity.kind === EntityKind.NPC
              ? ENTITY_COLOURS.npc
              : levelColour(entity.level, frame.playerVitals.level),
            frame.nameColorMode,
            frame.nameColorEmpire,
          ),
        ),
        title.text,
        title.colour,
        levelTail(entity.level),
      );
    }
    this.drawStallSign(
      x, top - nameLift - (named ? 18 : 2) - guildLift - markLift, entity.id, frame,
    );
  }

  private drawLootAt(
    x: number,
    y: number,
    scale: number,
    entity: RemoteEntity,
    frame: RenderFrame,
  ): void {
    const radius = Math.max(4, LOOT_RADIUS_MM * scale);
    const ownership = lootOwnership(
      entity.itemOwner,
      entity.itemPublicAtTick,
      frame.playerName,
      frame.serverTick,
    );
    if (!this.dropModelIds.has(entity.id)) {
      drawLoot(
        this.ctx,
        x,
        y,
        radius,
        entity.itemKind,
        frame.nowMs,
        entity.id,
        ownership.claimedByOther,
      );
    }
    if (entity.id === frame.targetId) this.drawTargetRing(x, y, radius);
    if (entity.name !== "") {
      this.drawName(x, y - radius - 8, entity.name, ownership.itemNameColour);
    }
    if (ownership.ownerLabel !== "") {
      // Owner above item, as two separate lines. The original positions the
      // owner first and shifts the ordinary item line down by 15 px
      // (`PythonTextTail.cpp:193-207`, `:705-736`).
      this.drawName(x, y - radius - 23, ownership.ownerLabel, OWNER_NAME_COLOUR);
    }
  }

  private drawStone(x: number, y: number, radius: number, vitals: Vitals): void {
    const ctx = this.ctx;
    const remaining = healthFraction(vitals.hp, vitals.maxHp);

    ctx.beginPath();
    for (let corner = 0; corner < 6; corner++) {
      // Started at -90 degrees so a point is at the top, not a flat edge.
      const angle = (corner / 6) * Math.PI * 2 - Math.PI / 2;
      const reach = radius * (corner % 2 === 0 ? 1 : 0.72);
      const px = x + Math.cos(angle) * reach;
      const py = y + Math.sin(angle) * reach;
      if (corner === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();

    if (remaining >= 1 || remaining <= 0) return;
    // Cracks spread from the centre as it breaks. Deterministic angles rather
    // than random ones: a stone that re-cracks differently every frame shimmers.
    ctx.strokeStyle = ENTITY_COLOURS.stoneCracked;
    ctx.lineWidth = Math.max(1, radius * 0.06);
    const cracks = Math.min(5, Math.floor((1 - remaining) * 6));
    for (let crack = 0; crack < cracks; crack++) {
      const angle = (crack / 5) * Math.PI * 2 + 0.4;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(angle) * radius * 0.9, y + Math.sin(angle) * radius * 0.9);
      ctx.stroke();
    }
  }

  private drawGuildBuilding(
    x: number,
    y: number,
    scale: number,
    entity: RemoteEntity,
    symbols?: ReadonlyMap<number, CanvasImageSource>,
    alpha = 1,
  ): void {
    const isInsignia = entity.buildingVnum === GUILD_SYMBOL_RACE;
    // A building is its own race, drawn from its own race script
    // (`root/playersettingmodule.py:1339-1340`, `InstanceBase.cpp:2009-2010`);
    // a race whose model loads nothing is no instance at all
    // (`InstanceBase.cpp:725-726`, `ActorInstanceData.cpp:59-64`).
    const species = isInsignia ? undefined : this.meshes.buildingModelKey(entity.buildingVnum);
    if (species === null) return;
    if (species !== undefined && this.meshes.status(species).kind === "ready") {
      const groundMm = this.heightField.at(entity.xMm, entity.yMm);
      this.meshes.draw(
        species, entity.id, entity.xMm, entity.yMm, groundMm, entity.heading, Gait.IDLE,
        this.camera.viewProj, this.paletteFor(species), null, species, null, alpha,
      );
      if (entity.name === "") return;
      const meshRadius = Math.max(7, 900 * scale);
      const top = this.labelTop(species, entity.xMm, entity.yMm, y, meshRadius, 0);
      this.drawName(x, top - 4, entity.name, "#e8d9a0");
      return;
    }

    const style = guildBuildingStyle(entity.buildingVnum);
    const radius = Math.max(7, 900 * scale);
    const width = radius * style.widthPx;
    const height = radius * style.heightPx;
    if (isInsignia && this.insigniaModelIds.has(entity.id)) {
      if (entity.name !== "") this.drawName(x, y - height * 0.9, entity.name, "#e8d9a0");
      return;
    }
    const angle = this.screenAngle(entity.heading);
    const ctx = this.ctx;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = "rgba(0, 0, 0, 0.32)";
    ctx.fillRect(-width * 0.54, -height * 0.44, width * 1.08, height * 0.9);
    ctx.fillStyle = style.wall;
    ctx.fillRect(-width * 0.5, -height * 0.5, width, height);
    ctx.fillStyle = style.roof;
    ctx.beginPath();
    ctx.moveTo(-width * 0.62, -height * 0.5);
    ctx.lineTo(0, -height * 0.84);
    ctx.lineTo(width * 0.62, -height * 0.5);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#302a2a";
    ctx.fillRect(-width * 0.11, height * 0.08, width * 0.22, height * 0.42);
    // `ChangeMaterial(GetGuildSymbolFileName(guild))` on race 14200 only
    // (`InstanceBase.cpp:832-838`); the symbol is 64 wide by 128 tall.
    const symbol = isInsignia ? symbols?.get(entity.guildId) : undefined;
    if (symbol !== undefined) ctx.drawImage(symbol, -height * 0.25, -height * 0.5, height * 0.5, height);
    ctx.restore();

    if (entity.name !== "") this.drawName(x, y - height * 0.9, entity.name, "#e8d9a0");
  }

  /** The bar itself: a dark frame, a coloured fill, nothing else. Geometry comes
   *  from `health.ts` so it can be asserted without a canvas. */
  private drawHealthBar(
    centreXPx: number,
    anchorYPx: number,
    vitals: Vitals,
    widthPx: number,
    heightPx: number,
  ): void {
    const ctx = this.ctx;
    const rect = barGeometry(centreXPx, anchorYPx, widthPx, heightPx, 0);
    const fraction = healthFraction(vitals.hp, vitals.maxHp);

    ctx.fillStyle = ENTITY_COLOURS.barBack;
    ctx.fillRect(rect.x - 1, rect.y - 1, rect.width + 2, rect.height + 2);
    ctx.fillStyle = healthColour(fraction);
    ctx.fillRect(rect.x, rect.y, barFillWidth(rect, fraction), rect.height);
    ctx.strokeStyle = ENTITY_COLOURS.barEdge;
    ctx.lineWidth = 1;
    ctx.strokeRect(rect.x - 0.5, rect.y - 0.5, rect.width + 1, rect.height + 1);
  }

  /** A white ring on the selected entity. Shape, not tint: a tinted target is
   *  indistinguishable from a target that is taking damage. */
  private drawTargetRing(x: number, y: number, radius: number): void {
    const ctx = this.ctx;
    ctx.strokeStyle = ENTITY_COLOURS.target;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, radius * 1.9, 0, Math.PI * 2);
    ctx.stroke();

    // Four ticks at the compass points, so the ring reads as a reticle rather
    // than as another circle in a scene made of circles.
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]] as const) {
      ctx.moveTo(x + dx * radius * 2.3, y + dy * radius * 2.3);
      ctx.lineTo(x + dx * radius * 2.9, y + dy * radius * 2.9);
    }
    ctx.stroke();
  }

  /** A white bloom that decays over ~200 ms. It says "that landed" and nothing
   *  else, which is why a miss never draws one. */
  private drawFlash(x: number, y: number, radius: number, intensity: number): void {
    if (intensity <= 0) return;
    const ctx = this.ctx;
    ctx.fillStyle = `rgba(255, 255, 255, ${(intensity * 0.55).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(x, y, radius * (1 + 0.9 * (1 - intensity)), 0, Math.PI * 2);
    ctx.fill();
  }

  /** Project the hit cue at the same height as the converted hit effect.
   * Canvas2D fallback bodies never have a mesh origin to align with, so they
   * retain the ground-centred cue that was authored for their silhouette. */
  private flashY(
    key: string,
    xMm: number,
    yMm: number,
    groundY: number,
    radius: number,
    liftMm = 0,
  ): number {
    if (this.meshes.status(key).kind !== "ready") return groundY;
    const effect = this.camera.project(
      xMm,
      yMm,
      this.heightField.at(xMm, yMm) + liftMm + HIT_FLASH_HEIGHT_MM,
    );
    return effect.behind ? groundY : flashAnchorY(groundY, radius, effect.y);
  }

  /** An expanding, fading ring where something died. */
  private drawDeath(mark: DeathMark, nowMs: number, proj: Projected): void {
    const phase = deathPhase(mark, nowMs);
    if (phase >= 1) return;
    const ctx = this.ctx;
    const x = proj.x;
    const y = proj.y;
    const radius = Math.max(4, MONSTER_RADIUS_MM * proj.scale);

    ctx.save();
    ctx.globalAlpha = 1 - phase;
    ctx.strokeStyle = ENTITY_COLOURS.death;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, radius * (0.6 + 2.2 * phase), 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawFloater(floater: Floater, frame: RenderFrame): void {
    const alpha = floaterAlpha(floater, frame.nowMs);
    if (alpha <= 0) return;
    const proj = this.groundProject(floater.xMm, floater.yMm);
    if (proj.behind) return;
    const ctx = this.ctx;
    const x = proj.x;
    const seen = this.lastSeen.get(floater.entityId);
    const meshHeightMm = seen === undefined || this.meshes.status(seen.key).kind !== "ready"
      ? null
      : this.meshes.heightMm(seen.key);
    const mount = floater.entityId === frame.playerId
      ? frame.playerMount ?? 0
      : frame.others.find((entity) => entity.id === floater.entityId)?.mount ?? 0;
    const modelTop = meshHeightMm === null
      ? null
      : this.camera.project(
          floater.xMm,
          floater.yMm,
          this.heightField.at(floater.xMm, floater.yMm) + this.mountLiftMm(mount) + meshHeightMm,
        );
    const radiusMm = seen?.kind === EntityKind.STONE
      ? STONE_RADIUS_MM
      : seen?.kind === EntityKind.MONSTER
        ? MONSTER_RADIUS_MM
        : PLAYER_RADIUS_MM;
    const anchorY = floaterAnchorY(
      proj.y,
      Math.max(3, radiusMm * proj.scale),
      modelTop?.behind === false ? modelTop.y : null,
    );
    const y = anchorY - floaterOffsetPx(floater, frame.nowMs);

    ctx.save();
    ctx.globalAlpha = alpha;
    // Criticals and incoming damage get the larger type. Everything else would
    // be shouting, and a screen where everything shouts says nothing.
    const large = floater.style === "crit" || floater.incoming;
    const size = large ? 19 : 14;
    ctx.font = `700 ${size}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const draw = damageDraw(
      Number.parseInt(floater.text, 10),
      { missed: floater.style === "miss", blocked: floater.style === "block" },
      floater.incoming ? "self" : "target",
    );
    // The original's own artwork, when it is on hand: 22 `.dds` sprites under
    // `damagevalue/`, imported by `tools/build_damagedigits.py`. A client that
    // has not fetched the atlas yet -- or cannot -- keeps the text below, so a
    // fight never loses its numbers over a missing file.
    if (this.drawDamageSprites(draw, floater, frame.nowMs, x, y, proj.scale)) {
      ctx.restore();
      return;
    }

    const glyphs: { readonly text: string; readonly dx: number }[] = [];
    if (draw.miss !== null) {
      glyphs.push({ text: floater.text, dx: 0 });
    } else if (draw.digits.length > 0) {
      // Legacy `FONT_WIDTH` is a 30-unit step sized for the original's digit
      // sprite; scaled to this font so a run of digits keeps its proportions.
      const step = (DAMAGE_FONT_WIDTH / 30) * size * 0.62;
      const centre = (step * (draw.digits.length - 1)) / 2;
      draw.digits.forEach((stem, index) => {
        glyphs.push({
          text: stem.slice(stem.lastIndexOf("_") + 1),
          dx: centre - step * index,
        });
      });
    } else {
      // Neither a number nor a miss -- a critical's trailing marker, or a zero.
      glyphs.push({ text: floater.text, dx: 0 });
    }

    for (const glyph of glyphs) {
      ctx.fillStyle = ENTITY_COLOURS.nameShadow;
      ctx.fillText(glyph.text, x + glyph.dx + 1, y + 1);
      ctx.fillStyle = FLOATER_COLOURS[floater.style];
      ctx.fillText(glyph.text, x + glyph.dx, y);
    }
    ctx.restore();
  }

  private drawDamageSprites(
    draw: DamageDraw,
    floater: Floater,
    nowMs: number,
    x: number,
    y: number,
    pxPerMm: number,
  ): boolean {
    const atlas = damageDigitAtlas();
    if (atlas === null) return false;
    const stems = draw.miss === null ? draw.digits : [draw.miss];
    if (stems.length === 0) return false;
    const phase = floaterPhase(floater, nowMs);

    // Resolved in full before anything is painted: a stem the sheet does not
    // carry must abort to the text path, not leave a gap in the middle of a
    // number that is already half drawn.
    const quads: { cell: readonly [number, number, number, number]; dx: number; dy: number;
                   w: number; h: number }[] = [];
    for (let index = 0; index < stems.length; index += 1) {
      const stem = stems[index]!;
      const cell = damageGlyphCell(stem);
      const family = atlas.manifest.families[damageGlyphFamily(stem)];
      if (cell === null || family === undefined) return false;
      const w = family.widthMm * sampleDamageCurve(family.scaleWidth, phase) * pxPerMm;
      const h = family.heightMm * sampleDamageCurve(family.scaleHeight, phase) * pxPerMm;
      if (!(w > 0) || !(h > 0)) return false;
      quads.push({
        cell,
        dx: x - family.stepMm * index * pxPerMm - w / 2,
        dy: y - h / 2,
        w,
        h,
      });
    }

    for (const quad of quads) {
      this.ctx.drawImage(
        atlas.image,
        quad.cell[0], quad.cell[1], quad.cell[2], quad.cell[3],
        quad.dx, quad.dy, quad.w, quad.h,
      );
    }
    return true;
  }

  private screenAngle(heading: number): number {
    return -(heading / HEADING_STEPS) * Math.PI * 2;
  }

  private labelTop(
    key: string,
    xMm: number,
    yMm: number,
    groundY: number,
    radius: number,
    liftMm = 0,
  ): number {
    if (this.meshes.status(key).kind !== "ready") return groundY - radius;
    const heightMm = this.meshes.heightMm(key);
    if (heightMm === null || heightMm <= 0) return groundY - radius;
    const head = this.camera.project(
      xMm,
      yMm,
      this.heightField.at(xMm, yMm) + liftMm + heightMm,
    );
    if (head.behind) return groundY - radius;
    // Never below the ground marker: a model whose bounding box is wrong, or a
    // camera looking almost straight down, must not push the label *under* the
    // character it belongs to.
    return Math.min(head.y, groundY - radius);
  }

  /** The material alpha this body is drawn at, advancing its ramp.
   *  `BlendAlphaValue` is called once, at the moment the state that starts a
   *  ramp changes, and runs from wherever the alpha had got to
   *  (`__BlendAlpha_Apply`, `ActorInstanceBlend.cpp:34-42`): a body is created
   *  at 0 and fades up over half a second (`InstanceBase.cpp:778-779`), and
   *  Eunhyeong takes the eyes allowed to see it down to 0.5 over a second
   *  (`InstanceBaseEffect.cpp:805-807`).
   */
  private bodyAlpha(entityId: number, nowMs: number, hidden: boolean): number {
    const current = this.alphaRamps.get(entityId);
    if (current === undefined) {
      const spawn = spawnRamp(nowMs);
      this.alphaRamps.set(entityId, spawn);
      return rampAlpha(spawn, nowMs);
    }
    const wanted = hidden ? STEALTH_VISIBLE_ALPHA : 1;
    if (current.toAlpha === wanted) return rampAlpha(current, nowMs);
    const started = stealthRamp(rampAlpha(current, nowMs), nowMs, hidden);
    this.alphaRamps.set(entityId, started);
    return rampAlpha(started, nowMs);
  }

  private drawBody(
    key: string,
    entityId: number,
    xMm: number,
    yMm: number,
    heading: number,
    gait: Gait,
    palette: CreaturePalette,
    fallback: (liftMm: number) => void,
    held: Readonly<Record<string, string>> | null = null,
    liftMm = 0,
    forceFallback = false,
    verticalShakeMm = 0,
    alpha = 1,
    mobColor = 0,
    castsShadow = false,
  ): boolean {
    // A hit shakes the actor, not its ground contact: the transient Z offset is
    // folded into the body, its fallback silhouette, and callers' attached parts.
    const visualLiftMm = liftMm + verticalShakeMm;
    if (forceFallback) {
      fallback(visualLiftMm);
      return false;
    }
    const status = this.meshes.status(key);
    if (status.kind === "ready") {
      const groundMm = this.heightField.at(xMm, yMm);
      this.meshes.setShade?.(entityId, this.shadeFor(entityId, xMm, yMm, gait));
      this.meshes.draw(
        key, entityId, xMm, yMm, groundMm + visualLiftMm, heading, gait,
        this.camera.viewProj, palette, held, key, null, alpha, mobColor,
      );
      if (castsShadow) this.meshes.castShadow?.(key, held);
      this.followAttachedEffects(entityId, key, xMm, yMm, groundMm + visualLiftMm, heading);
      this.followEffectBones(entityId, key);
      this.keepVolumes(entityId, key, xMm, yMm, groundMm + visualLiftMm, heading);
      return true;
    }
    if (status.kind === "loading") {
      return false;
    }
    this.logFallback(key, status);
    fallback(visualLiftMm);
    return false;
  }

  private drawDefaultHair(
    race: number,
    hairPart: number,
    entityId: number,
    xMm: number,
    yMm: number,
    heading: number,
    gait: Gait,
    palette: CreaturePalette,
    liftMm: number,
    bodyKey: string,
    alpha = 1,
    castsShadow = false,
  ): string | null {
    const key = this.meshes.hairModelKey(race, hairPart);
    if (key === null || this.meshes.status(key).kind !== "ready") return null;
    this.meshes.draw(
      key, hairEntityId(entityId), xMm, yMm, this.heightField.at(xMm, yMm) + liftMm,
      heading, gait, this.camera.viewProj, palette, null, bodyKey, entityId, alpha,
    );
    if (castsShadow) this.meshes.castShadow?.(key);
    return key;
  }

  /** Bones the hair draw immediately above skinned with -- read straight off
   *  the renderer, the way the held offset already is. */
  private hairBonesJustDrawn(): number {
    return this.meshes.skinnedBoneCount();
  }

  /** Record the successful body and hair draws as one character assembly.
   * The sole call sites are immediately after the real draw calls, so a part
   * cannot appear here merely because an item or a pack lookup named it. */
  private recordCharacterParts(
    entityId: number,
    body: string,
    hair: string | null,
    held: Readonly<Record<string, string>> | null,
    attackClip: ClipName,
    hairBones = 0,
  ): void {
    const characters = this.renderedCharacters ??= new Map();
    const anchor = held === null ? null : this.meshes.weaponAnchor();
    if (anchor === null) this.weaponAnchors.delete(entityId);
    else this.weaponAnchors.set(entityId, anchor);
    const anchorLeft = held === null ? null : this.meshes.weaponAnchorLeft();
    if (anchorLeft === null) this.weaponAnchorsLeft.delete(entityId);
    else this.weaponAnchorsLeft.set(entityId, anchorLeft);
    this.sampleWeaponTraces(entityId, body, held, anchor, anchorLeft);
    const previous = characters.get(entityId);
    const sameFrame = previous?.frame === this.renderedFrame;
    characters.set(entityId, {
      frame: this.renderedFrame,
      body,
      hair,
      bodyDraws: (sameFrame ? previous.bodyDraws : 0) + 1,
      hairDraws: (sameFrame ? previous.hairDraws : 0) + (hair === null ? 0 : 1),
      hairBones,
      held: held === null ? [] : [...new Set(Object.values(held))],
      activeClip: this.meshes.activeClip(entityId),
      heldOffsetMm: this.meshes.heldOffset(),
      attackClip,
    });
  }

  private drawMount(
    rider: {
      readonly id: number;
      readonly mount: number;
      readonly xMm: number;
      readonly yMm: number;
      readonly heading: number;
      readonly gait: Gait;
    },
    screenX: number,
    screenY: number,
    scale: number,
    castsShadow = false,
  ): MountDrawResult {
    const key = rider.mount === 0 ? undefined : MOUNT_MESH_KEYS[rider.mount];
    // A vnum this build has no key for draws nothing rather than drawing some
    // other animal: the server has named a body the pack was not built with.
    if (key === undefined) {
      this.meshes.seatRider?.(rider.id, null);
      return { liftMm: 0, fallbackOverlay: false };
    }

    const status = this.meshes.status(key);
    if (status.kind === "ready") {
      const groundMm = this.heightField.at(rider.xMm, rider.yMm);
      // `SetMaterialColor` hands the rider's colour to its horse
      // (`ActorInstanceRender.cpp:18-21`).
      this.meshes.setShade?.(-rider.id, this.shadeFor(rider.id, rider.xMm, rider.yMm, rider.gait));
      this.meshes.draw(
        key, -rider.id, rider.xMm, rider.yMm, groundMm,
        rider.heading, rider.gait, this.camera.viewProj, MOUNT_PALETTE, null,
      );
      // `m_pkHorse->RenderToShadowMap()` under its rider's own test (`ActorInstanceRender.cpp:383-384`).
      if (castsShadow) this.meshes.castShadow?.(key);
      // `__AttachHorseSaddle` (`InstanceBase.cpp:247-252`): the rider sits on the
      // saddle bone the horse was just posed with.
      const seat = this.meshes.saddle?.() ?? null;
      this.meshes.seatRider?.(rider.id, seat);
      return {
        liftMm: seat !== null ? seat[14]! - groundMm : this.meshes.heightMm(key) ?? MOUNT_FALLBACK_HEIGHT_MM,
        fallbackOverlay: false,
      };
    }

    // No horse in the pack. A quadruped silhouette, which is what every
    // monster on this map falls back to and is honest about being a stand-in --
    // the alternative is a man sprinting on foot, which is the bug this whole
    // field exists to fix.
    this.logFallback(key, status);
    this.meshes.seatRider?.(rider.id, null);
    drawCreature(this.ctx, "quadruped", {
      x: screenX,
      y: screenY,
      radius: Math.max(4, MONSTER_RADIUS_MM * scale) * 1.5,
      angle: this.screenAngle(rider.heading),
      distanceMm: this.gait.peek(rider.id),
      resting: rider.gait === Gait.IDLE,
      palette: MOUNT_PALETTE,
    });
    return { liftMm: MOUNT_FALLBACK_HEIGHT_MM, fallbackOverlay: true };
  }

  /** One console line per distinct (key, reason), ever -- not per frame, and
   *  not repeated once already said. A silhouette with no explanation reads
   *  as a bug; the same silhouette with a reason in the log is a known,
   *  bounded gap someone can act on or ignore. */
  private logFallback(key: string, status: Exclude<MeshStatus, { readonly kind: "ready" }>): void {
    if (status.kind === "loading") return; // Transient: nothing to report yet.
    const warnKey = `${key}:${status.kind}`;
    if (this.warnedFallback.has(warnKey)) return;
    this.warnedFallback.add(warnKey);
    console.warn(`${key}: drawing the silhouette (${status.reason})`);
  }

  /** Match the elevation drawMount applies to a rider's body. */
  private mountLiftMm(mount: number): number {
    if (mount === 0) return 0;
    const key = MOUNT_MESH_KEYS[mount];
    if (key === undefined) return 0;
    const height = this.meshes.heightMm(key);
    return height !== null && this.meshes.status(key).kind === "ready"
      ? height
      : MOUNT_FALLBACK_HEIGHT_MM;
  }

  private bodyFor(armourVnum: number, race: number): string {
    return bodyKeyFor(this.meshes, armourVnum, race);
  }

  private heldFor(weaponVnum: number, mounted = false, dressed = false): Readonly<Record<string, string>> | null {
    return heldPartsFor(this.meshes, weaponVnum, mounted, dressed);
  }

  private meshKeyFor(entity: RemoteEntity): string {
    return meshKeyFor(this.meshes, entity);
  }

  private drawGmMark(x: number, y: number, level: number, entityId: number): number {
    if (level <= 0) {
      this.markRects.delete(entityId);
      return 0;
    }
    const sprite = gmMarkImage;
    if (sprite === null || !sprite.complete || sprite.naturalWidth === 0) {
      this.markRects.delete(entityId);
      return 0;
    }
    const left = x - GM_MARK_WIDTH_PX / 2;
    const top = y - GM_MARK_HEIGHT_PX;
    this.ctx.drawImage(sprite, left, top, GM_MARK_WIDTH_PX, GM_MARK_HEIGHT_PX);
    this.markRects.set(entityId, {
      x: left,
      y: top,
      width: GM_MARK_WIDTH_PX,
      height: GM_MARK_HEIGHT_PX,
    });
    return GM_MARK_HEIGHT_PX + GM_MARK_GAP_PX;
  }

  private drawChatTail(x: number, y: number, text: string | null): number {
    if (text === null) return 0;
    this.drawName(x, y, text, CHAT_TAIL_COLOUR);
    return CHAT_TAIL_NAME_LIFT_PX;
  }

  private drawPlate(
    x: number,
    y: number,
    name: string,
    nameColour: string,
    title: string,
    titleColour: string,
    level: string,
  ): void {
    const ctx = this.ctx;
    ctx.font = PLATE_FONT;
    const nameWidth = name === "" ? 0 : ctx.measureText(name).width;
    // The right edge both hanging lines are measured back from.
    const rightEdge = x - nameWidth / 2;
    if (title !== "") this.drawName(rightEdge, y, title, titleColour, "right");
    if (level !== "") {
      const titleWidth = title === "" ? 0 : ctx.measureText(title).width;
      this.drawName(rightEdge - titleWidth, y, level, LEVEL_TAIL_COLOUR, "right");
    }
    const shift = title === "" ? PLATE_NAME_SHIFT_PX : PLATE_NAME_SHIFT_TITLED_PX;
    if (name !== "") this.drawName(x + shift, y, name, nameColour);
  }

  private drawGuildTail(
    x: number,
    y: number,
    guildName: string,
    emblem?: readonly number[],
  ): number {
    if (guildName === "") return 0;
    this.drawName(x + GUILD_TAIL_SHIFT_PX, y - GUILD_TAIL_LIFT_PX, guildName, GUILD_TAIL_COLOUR);
    const image = emblem === undefined ? null : this.guildMarkImage(emblem);
    if (image !== null) {
      // The mark only when a sign was served for the guild, as
      // `RegisterCharacterTextTail` loads one only for a known mark id
      // (`PythonTextTail.cpp:547-562`).
      this.ctx.font = PLATE_FONT;
      const nameLeft = x + GUILD_TAIL_SHIFT_PX - this.ctx.measureText(guildName).width / 2;
      const rect = guildMarkRect(nameLeft, y);
      this.ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height);
    }
    return GUILD_TAIL_LIFT_PX;
  }

  /** One served sign as a 16x12 image, built once per sign array. */
  private guildMarkImage(emblem: readonly number[]): CanvasImageSource | null {
    const cached = this.guildMarks.get(emblem);
    if (cached !== undefined) return cached;
    const pixels = guildMarkPixels(emblem);
    if (pixels === null || typeof document === "undefined") return null;
    const canvas = document.createElement("canvas");
    canvas.width = GUILD_MARK_WIDTH_PX;
    canvas.height = GUILD_MARK_HEIGHT_PX;
    const context = canvas.getContext("2d");
    if (context === null) return null;
    context.putImageData(new ImageData(pixels, GUILD_MARK_WIDTH_PX, GUILD_MARK_HEIGHT_PX), 0, 0);
    this.guildMarks.set(emblem, canvas);
    return canvas;
  }

  private drawName(
    x: number,
    y: number,
    name: string,
    colour: string,
    align: CanvasTextAlign = "center",
  ): void {
    const ctx = this.ctx;
    ctx.font = PLATE_FONT;
    ctx.textAlign = align;
    ctx.textBaseline = "bottom";
    // A shadow rather than an outline: names sit over both dark walls and bright
    // water, and a single stroke colour is unreadable on one of the two.
    ctx.fillStyle = ENTITY_COLOURS.nameShadow;
    ctx.fillText(name, x + 1, y + 1);
    ctx.fillStyle = colour;
    ctx.fillText(name, x, y);
  }

  private drawStallSign(x: number, y: number, entityId: number, frame: RenderFrame): void {
    const sign = stallSignFor(entityId, frame.playerId, frame.stallSigns, frame.showSalesText);
    if (sign === "") return;
    this.drawName(x, y, sign, "#e8d9a0");
  }

  /** The joystick, if a thumb is on it. Screen space, above everything. */
  drawJoystick(
    origin: { x: number; y: number },
    knob: { x: number; y: number },
    radiusPx: number,
  ): void {
    const ctx = this.ctx;
    ctx.strokeStyle = "rgba(233, 238, 245, 0.35)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(origin.x, origin.y, radiusPx, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = "rgba(233, 238, 245, 0.55)";
    ctx.beginPath();
    ctx.arc(knob.x, knob.y, radiusPx * 0.4, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** How far a monster may be and still wear its name, in world millimetres. */
const NAMEPLATE_RANGE_MM = 16_000;

export function shouldName(entity: RemoteEntity, frame: RenderFrame): boolean {
  // `GameWindow.__IsShowName` returns true from the local setting before it
  // considers its temporary hotkey flag (`game.py:1133-1142`).  The browser
  // does not have that hotkey, so this is the exact durable half: the checked
  // setting turns a distant monster's name back on without changing its AOI.
  if (isStealthed(entity)) return false;
  if (frame.alwaysShowNames === true) return true;
  if (entity.id === frame.targetId) return true;
  if (entity.kind !== EntityKind.MONSTER) return true;

  const dx = entity.xMm - frame.player.xMm;
  const dy = entity.yMm - frame.player.yMm;
  return dx * dx + dy * dy <= NAMEPLATE_RANGE_MM * NAMEPLATE_RANGE_MM;
}

export function chatTailFor(entity: RemoteEntity, text: string | null): string | null {
  return isStealthed(entity) ? null : text;
}

export function plateShown(
  entity: RemoteEntity,
  frame: RenderFrame,
  speaking: boolean,
): boolean {
  if (isStealthed(entity)) return false;
  return shouldName(entity, frame) || frame.showNameHeld === true || speaking;
}

/** `IsShowDamage()` guards the original damage queue before it can render
 * (`InstanceBaseEffect.cpp:87-97`).  Kept pure so the visible-off behaviour is
 * testable without a canvas. */
export function shouldShowDamage(frame: Pick<RenderFrame, "showDamage">): boolean {
  return frame.showDamage !== false;
}

/** The local seller keeps their own shop board even with other sales text
 * disabled (`uiprivateshopbuilder.py:102-114`).  This is presentation only:
 * the map already contains server-sent text and no client string becomes an
 * action or a shop identity. */
export function stallSignFor(
  entityId: number,
  playerId: number,
  signs: ReadonlyMap<number, string> | undefined,
  showSalesText: boolean | undefined,
): string {
  const sign = signs?.get(entityId)?.trim() ?? "";
  return sign !== "" && (showSalesText !== false || entityId === playerId) ? sign : "";
}


/** Drop the oldest keys from a set of monotonically increasing event ids once
 *  it grows past `keep`, halving it rather than clearing it so that an event
 *  still being reported is never re-fired. */
function headingToRadians(headingSteps: number): number {
  return (headingSteps / HEADING_STEPS) * Math.PI * 2 + Math.PI / 2;
}

function forget(keys: Set<number>, keep: number): void {
  if (keys.size <= keep) return;
  let highest = -Infinity;
  for (const key of keys) highest = Math.max(highest, key);
  const floor = highest - keep / 2;
  for (const key of keys) if (key < floor) keys.delete(key);
}

/** Kind decides the palette; gait only dims it. A monster standing still is
 *  still a monster, so the idle shade stays within the same hue. */
function entityColour(entity: RemoteEntity): string {
  if (entity.kind === EntityKind.MONSTER) {
    return entity.gait === Gait.IDLE ? ENTITY_COLOURS.monsterIdle : ENTITY_COLOURS.monster;
  }
  if (entity.kind === EntityKind.STONE) return ENTITY_COLOURS.stone;
  if (entity.kind === EntityKind.NPC) return ENTITY_COLOURS.npc;
  if (entity.kind === EntityKind.ITEM) return ENTITY_COLOURS.loot;
  return entity.gait === Gait.IDLE ? ENTITY_COLOURS.otherIdle : ENTITY_COLOURS.other;
}
